import React, { useState, useEffect, useRef, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, increment, update, get } from 'firebase/database';
import char1Svg from './assets/1.svg';
import char2Svg from './assets/2.svg';
import char3Svg from './assets/3.svg';
import char4Svg from './assets/4.svg';
import char5Svg from './assets/5.svg';
import char6Svg from './assets/6.svg';

// ============ FIREBASE CONFIG ============
const firebaseConfig = {
  apiKey: "AIzaSyAQLerT7nID2nFNBejKr5ArZboM4bCePvw",
  authDomain: "word-slide-game-99-c2f68.firebaseapp.com",
  databaseURL: "https://word-slide-game-99-c2f68-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "word-slide-game-99-c2f68",
  storageBucket: "word-slide-game-99-c2f68.firebasestorage.app",
  messagingSenderId: "983146388256",
  appId: "1:983146388256:web:512e3041527b37090bd545",
  measurementId: "G-EEQ4WF6WMY"
};

const app = initializeApp(firebaseConfig);
const database = getDatabase(app);

// ============ GAME COUNTER ============
const GameCounter = {
  counted: false,
  spinCount: 0,
  
  addSpin() {
    this.spinCount++;
    if (this.spinCount >= 4 && !this.counted) {
      this.counted = true;
      this.incrementGameCount();
    }
  },
  
  reset() {
    this.counted = false;
    this.spinCount = 0;
  },
  
  async incrementGameCount() {
    try {
      const counterRef = ref(database, 'stats/gamesPlayed');
      await update(ref(database, 'stats'), {
        gamesPlayed: increment(1),
        lastPlayed: new Date().toISOString()
      });
      console.log('Game counted!');
    } catch (error) {
      console.log('Counter update failed:', error);
    }
  },
  
  async getCount() {
    try {
      const snapshot = await get(ref(database, 'stats/gamesPlayed'));
      return snapshot.val() || 0;
    } catch {
      return 0;
    }
  }
};

// ============ SOUND SYSTEM ============
const SoundManager = {
  ctx: null,
  enabled: true,
  
  init() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  },
  
  play(type) {
    if (!this.enabled) return;
    this.init();
    const ctx = this.ctx;
    const now = ctx.currentTime;
    
    switch(type) {
      case 'keyPress': {
        // Short click sound
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.setValueAtTime(800, now);
        osc.frequency.exponentialRampToValueAtTime(600, now + 0.05);
        gain.gain.setValueAtTime(0.1, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.05);
        osc.start(now);
        osc.stop(now + 0.05);
        break;
      }
      
      case 'submit': {
        // Success chime - two notes
        [0, 0.1].forEach((delay, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.setValueAtTime(i === 0 ? 523 : 659, now + delay); // C5, E5
          gain.gain.setValueAtTime(0.15, now + delay);
          gain.gain.exponentialRampToValueAtTime(0.01, now + delay + 0.15);
          osc.start(now + delay);
          osc.stop(now + delay + 0.15);
        });
        break;
      }
      
      case 'invalid': {
        // Error buzz
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sawtooth';
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.setValueAtTime(150, now);
        gain.gain.setValueAtTime(0.15, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
        osc.start(now);
        osc.stop(now + 0.2);
        break;
      }
      
      case 'wheelSpin': {
        // Spinning tick sounds - finish well before 4s wheel animation
        for (let i = 0; i < 30; i++) {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          // Quick start, exponential slowdown, finish by 3s
          const t = now + (i * 0.05) + (i * i * 0.003);
          if (t > now + 3) break;
          osc.frequency.setValueAtTime(1400 - i * 30, t);
          gain.gain.setValueAtTime(0.06, t);
          gain.gain.exponentialRampToValueAtTime(0.01, t + 0.025);
          osc.start(t);
          osc.stop(t + 0.025);
        }
        break;
      }
      
      case 'wheelLand': {
        // Landing fanfare - bright triumphant sound
        const notes = [523, 659, 784]; // C5, E5, G5
        notes.forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.setValueAtTime(freq, now + i * 0.1);
          gain.gain.setValueAtTime(0.15, now + i * 0.1);
          gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.1 + 0.3);
          osc.start(now + i * 0.1);
          osc.stop(now + i * 0.1 + 0.3);
        });
        break;
      }
      
      case 'move': {
        // Piece moving - ascending blips
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.setValueAtTime(300, now);
        osc.frequency.linearRampToValueAtTime(600, now + 0.15);
        gain.gain.setValueAtTime(0.1, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
        osc.start(now);
        osc.stop(now + 0.15);
        break;
      }
      
      case 'stair': {
        // Mellow ascending whoosh - soft and uplifting
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        
        // Soft sine wave whoosh
        osc.type = 'sine';
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.setValueAtTime(200, now);
        osc.frequency.exponentialRampToValueAtTime(600, now + 0.6);
        osc.frequency.exponentialRampToValueAtTime(800, now + 1);
        gain.gain.setValueAtTime(0.06, now);
        gain.gain.setValueAtTime(0.08, now + 0.3);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 1);
        osc.start(now);
        osc.stop(now + 1);
        
        // Gentle shimmer layer
        osc2.type = 'triangle';
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.frequency.setValueAtTime(400, now);
        osc2.frequency.exponentialRampToValueAtTime(1200, now + 0.8);
        gain2.gain.setValueAtTime(0.03, now);
        gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.9);
        osc2.start(now);
        osc2.stop(now + 0.9);
        break;
      }
      
      case 'slide': {
        // Sad trombone - "wah wah wah wahhh" (quieter)
        const notes = [350, 330, 311, 233];
        const durations = [0.25, 0.25, 0.25, 0.6];
        let time = now;
        
        notes.forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sawtooth';
          osc.connect(gain);
          gain.connect(ctx.destination);
          
          osc.frequency.setValueAtTime(freq, time);
          if (i === 3) {
            osc.frequency.setValueAtTime(freq, time);
            osc.frequency.linearRampToValueAtTime(freq * 0.95, time + 0.15);
            osc.frequency.linearRampToValueAtTime(freq * 0.9, time + 0.4);
          }
          
          gain.gain.setValueAtTime(0.07, time);
          gain.gain.setValueAtTime(0.05, time + durations[i] * 0.8);
          gain.gain.exponentialRampToValueAtTime(0.01, time + durations[i]);
          
          osc.start(time);
          osc.stop(time + durations[i]);
          time += durations[i];
        });
        break;
      }
      
      case 'win': {
        // Epic victory fanfare! Multi-layered celebration
        
        // Triumphant brass-style melody
        const melody = [
          { freq: 392, time: 0, dur: 0.15 },      // G4
          { freq: 392, time: 0.15, dur: 0.15 },   // G4
          { freq: 392, time: 0.3, dur: 0.15 },    // G4
          { freq: 311, time: 0.45, dur: 0.3 },    // Eb4
          { freq: 349, time: 0.8, dur: 0.1 },     // F4
          { freq: 392, time: 0.95, dur: 0.15 },   // G4
          { freq: 311, time: 1.15, dur: 0.25 },   // Eb4
          { freq: 392, time: 1.45, dur: 0.5 },    // G4 (hold)
          { freq: 523, time: 2, dur: 0.15 },      // C5
          { freq: 523, time: 2.15, dur: 0.15 },   // C5
          { freq: 523, time: 2.3, dur: 0.15 },    // C5
          { freq: 523, time: 2.45, dur: 0.6 },    // C5 (hold)
        ];
        
        melody.forEach(({ freq, time, dur }) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sawtooth';
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.setValueAtTime(freq, now + time);
          gain.gain.setValueAtTime(0.12, now + time);
          gain.gain.setValueAtTime(0.1, now + time + dur * 0.8);
          gain.gain.exponentialRampToValueAtTime(0.01, now + time + dur);
          osc.start(now + time);
          osc.stop(now + time + dur);
        });
        
        // Harmony layer
        const harmony = [
          { freq: 523, time: 0, dur: 0.45 },      // C5
          { freq: 466, time: 0.45, dur: 0.35 },   // Bb4
          { freq: 523, time: 0.95, dur: 0.5 },    // C5
          { freq: 659, time: 2, dur: 1 },         // E5
        ];
        
        harmony.forEach(({ freq, time, dur }) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'triangle';
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.setValueAtTime(freq, now + time);
          gain.gain.setValueAtTime(0.08, now + time);
          gain.gain.exponentialRampToValueAtTime(0.01, now + time + dur);
          osc.start(now + time);
          osc.stop(now + time + dur);
        });
        
        // Celebration sparkles
        for (let i = 0; i < 8; i++) {
          const sparkle = ctx.createOscillator();
          const sparkGain = ctx.createGain();
          sparkle.type = 'sine';
          const t = now + 2.5 + i * 0.1;
          sparkle.frequency.setValueAtTime(1200 + Math.random() * 800, t);
          sparkGain.gain.setValueAtTime(0.06, t);
          sparkGain.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
          sparkle.connect(sparkGain);
          sparkGain.connect(ctx.destination);
          sparkle.start(t);
          sparkle.stop(t + 0.15);
        }
        
        // Final triumphant chord
        const finalChord = [523, 659, 784, 1047]; // C major
        finalChord.forEach((freq) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sawtooth';
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.setValueAtTime(freq, now + 3);
          gain.gain.setValueAtTime(0.1, now + 3);
          gain.gain.setValueAtTime(0.08, now + 3.5);
          gain.gain.exponentialRampToValueAtTime(0.01, now + 4);
          osc.start(now + 3);
          osc.stop(now + 4);
        });
        break;
      }
      
      case 'timeout': {
        // Time's up - descending tone
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.setValueAtTime(500, now);
        osc.frequency.exponentialRampToValueAtTime(200, now + 0.4);
        gain.gain.setValueAtTime(0.15, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
        osc.start(now);
        osc.stop(now + 0.4);
        break;
      }

      default:
        break;
    }
  },
  
  // Background music system
  musicNodes: null,
  musicPlaying: false,
  musicInterval: null,
  musicType: null,
  
  startMusic(type = 'game') {
    if (!this.enabled || this.musicPlaying) return;
    this.init();
    this.musicPlaying = true;
    this.musicType = type;
    
    const ctx = this.ctx;
    
    // Create master gain for music
    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(type === 'anticipation' ? 0.06 : 0.07, ctx.currentTime);
    masterGain.connect(ctx.destination);
    
    let beat = 0;
    let bar = 0;
    
    const playBeat = () => {
      if (!this.musicPlaying || !this.enabled) {
        this.stopMusic();
        return;
      }
      
      const now = ctx.currentTime;
      
      if (type === 'anticipation') {
        // Anticipation music - building tension, playful
        const bassNotes = [130, 130, 146, 146, 164, 164, 174, 174]; // Building up
        const bassNote = bassNotes[bar % 8];
        
        // Pulsing bass
        if (beat % 2 === 0) {
          const bass = ctx.createOscillator();
          const bassGain = ctx.createGain();
          bass.type = 'sine';
          bass.frequency.setValueAtTime(bassNote, now);
          bassGain.gain.setValueAtTime(0.2, now);
          bassGain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
          bass.connect(bassGain);
          bassGain.connect(masterGain);
          bass.start(now);
          bass.stop(now + 0.2);
        }
        
        // Ticking/anticipation sound
        const tick = ctx.createOscillator();
        const tickGain = ctx.createGain();
        tick.type = 'sine';
        tick.frequency.setValueAtTime(800 + (beat % 4) * 100, now);
        tickGain.gain.setValueAtTime(0.08, now);
        tickGain.gain.exponentialRampToValueAtTime(0.01, now + 0.05);
        tick.connect(tickGain);
        tickGain.connect(masterGain);
        tick.start(now);
        tick.stop(now + 0.05);
        
        // Sparkle arpeggios every 4 beats
        if (beat % 4 === 0) {
          [0, 0.1, 0.2].forEach((delay, i) => {
            const spark = ctx.createOscillator();
            const sparkGain = ctx.createGain();
            spark.type = 'sine';
            spark.frequency.setValueAtTime(600 + i * 200 + bar * 20, now + delay);
            sparkGain.gain.setValueAtTime(0.06, now + delay);
            sparkGain.gain.exponentialRampToValueAtTime(0.01, now + delay + 0.15);
            spark.connect(sparkGain);
            sparkGain.connect(masterGain);
            spark.start(now + delay);
            spark.stop(now + delay + 0.15);
          });
        }
      } else {
        // Game music - fun, energetic, arcade-style
        const bassPattern = [
          [262, 262, 196, 196, 220, 220, 175, 175], // C C G G A A F F
        ][0];
        const bassNote = bassPattern[bar % 8] / 2;
        
        // Punchy bass on 1 and 3
        if (beat === 0 || beat === 2) {
          const bass = ctx.createOscillator();
          const bassGain = ctx.createGain();
          bass.type = 'square';
          bass.frequency.setValueAtTime(bassNote, now);
          bass.frequency.setValueAtTime(bassNote * 1.01, now + 0.05);
          bassGain.gain.setValueAtTime(0.15, now);
          bassGain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
          bass.connect(bassGain);
          bassGain.connect(masterGain);
          bass.start(now);
          bass.stop(now + 0.15);
        }
        
        // Synth stabs on 2 and 4 (offbeat groove)
        if (beat === 1 || beat === 3) {
          const chords = [
            [330, 392, 523], // C maj
            [294, 392, 494], // G maj
            [330, 440, 523], // A min
            [262, 349, 440], // F maj
          ];
          const chord = chords[Math.floor(bar / 2) % 4];
          chord.forEach((freq) => {
            const synth = ctx.createOscillator();
            const synthGain = ctx.createGain();
            synth.type = 'square';
            synth.frequency.setValueAtTime(freq, now);
            synthGain.gain.setValueAtTime(0.06, now);
            synthGain.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
            synth.connect(synthGain);
            synthGain.connect(masterGain);
            synth.start(now);
            synth.stop(now + 0.1);
          });
        }
        
        // Hi-hat pattern - every eighth note with accents
        const hihat = ctx.createOscillator();
        const hihatGain = ctx.createGain();
        hihat.type = 'square';
        hihat.frequency.setValueAtTime(1200 + Math.random() * 200, now);
        const accent = (beat === 0 || beat === 2) ? 0.06 : 0.03;
        hihatGain.gain.setValueAtTime(accent, now);
        hihatGain.gain.exponentialRampToValueAtTime(0.001, now + 0.03);
        hihat.connect(hihatGain);
        hihatGain.connect(masterGain);
        hihat.start(now);
        hihat.stop(now + 0.03);
        
        // Melodic hook every 8 bars
        if (bar % 8 === 0 && beat === 0) {
          const melody = [523, 587, 659, 523, 494, 440];
          melody.forEach((freq, i) => {
            const mel = ctx.createOscillator();
            const melGain = ctx.createGain();
            mel.type = 'sine';
            mel.frequency.setValueAtTime(freq, now + i * 0.12);
            melGain.gain.setValueAtTime(0.1, now + i * 0.12);
            melGain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.12 + 0.1);
            mel.connect(melGain);
            melGain.connect(masterGain);
            mel.start(now + i * 0.12);
            mel.stop(now + i * 0.12 + 0.1);
          });
        }
      }
      
      // Advance beat
      beat++;
      if (beat >= 4) {
        beat = 0;
        bar++;
      }
    };
    
    // Store reference for cleanup
    this.musicNodes = { masterGain };
    
    // Play at ~130 BPM for game, ~100 BPM for anticipation
    const bpm = type === 'anticipation' ? 100 : 130;
    const interval = 60000 / bpm / 2; // eighth notes
    playBeat();
    this.musicInterval = setInterval(playBeat, interval);
  },
  
  stopMusic() {
    this.musicPlaying = false;
    this.musicType = null;
    if (this.musicInterval) {
      clearInterval(this.musicInterval);
      this.musicInterval = null;
    }
    this.musicNodes = null;
  },
  
  toggle() {
    this.enabled = !this.enabled;
    if (!this.enabled) {
      this.stopMusic();
    }
    return this.enabled;
  }
};

// Map SVG filenames to imports
const SVG_MAP = {
  '1.svg': char1Svg,
  '2.svg': char2Svg,
  '3.svg': char3Svg,
  '4.svg': char4Svg,
  '5.svg': char5Svg,
  '6.svg': char6Svg,
};

// Helper to render character avatar (emoji or SVG)
const CharacterAvatar = ({ character, size = 'medium' }) => {
  const sizeClass = size === 'large' ? 'avatar-lg' : size === 'small' ? 'avatar-sm' : size === 'tiny' ? 'avatar-tiny' : 'avatar-md';
  if (character?.svg) {
    return <img src={SVG_MAP[character.svg]} alt={character.name} className={`char-svg ${sizeClass}`} />;
  }
  return <span className={sizeClass}>{character?.emoji}</span>;
};

// Expanded word list
const WORDS = new Set([
  'ace','act','add','age','ago','aid','aim','air','all','and','ant','any','ape','arc','are','ark','arm','art','ash','ask','ate','awe','axe','bad','bag','ban','bar','bat','bay','bed','bee','beg','bet','bid','big','bin','bit','bow','box','boy','bud','bug','bus','but','buy','cab','can','cap','car','cat','cop','cot','cow','cry','cub','cup','cut','dad','dam','day','den','dew','did','die','dig','dim','dip','dog','dot','dry','dub','dud','due','dug','dye','ear','eat','eel','egg','ego','elf','elk','elm','emu','end','era','eve','eye','fan','far','fat','fax','fed','fee','few','fig','fin','fir','fit','fix','fly','foe','fog','for','fox','fry','fun','fur','gag','gap','gas','gel','gem','get','gig','god','got','gum','gun','gut','guy','gym','had','ham','has','hat','hay','hem','hen','her','hid','him','hip','his','hit','hog','hop','hot','how','hub','hue','hug','hum','hut','ice','icy','ill','imp','ink','inn','ion','irk','its','ivy','jab','jam','jar','jaw','jay','jet','jig','job','jog','jot','joy','jug','jut','keg','key','kid','kin','kit','lab','lad','lag','lap','law','lay','led','leg','let','lid','lie','lip','lit','log','lot','low','lug','mad','man','map','mat','max','may','men','met','mid','mix','mob','mom','mop','mud','mug','mum','nab','nag','nap','net','new','nil','nip','nod','nor','not','now','nub','nun','nut','oak','oar','oat','odd','off','oil','old','one','opt','orb','ore','our','out','owe','owl','own','pad','pal','pan','par','pat','paw','pay','pea','peg','pen','pep','per','pet','pie','pig','pin','pit','ply','pod','pop','pot','pro','pry','pub','pug','pun','pup','put','rag','ram','ran','rap','rat','raw','ray','red','rib','rid','rig','rim','rip','rob','rod','rot','row','rub','rug','rum','run','rut','rye','sad','sag','sap','sat','saw','say','sea','set','sew','she','shy','sin','sip','sir','sis','sit','six','ski','sky','sly','sob','sod','son','sop','sow','soy','spa','spy','sub','sum','sun','tab','tag','tan','tap','tar','tax','tea','ten','the','tie','tin','tip','toe','ton','too','top','tot','tow','toy','try','tub','tug','two','urn','use','van','vat','vet','via','vie','vow','wad','wag','war','was','wax','way','web','wed','wee','wet','who','why','wig','win','wit','woe','wok','won','woo','wow','yak','yam','yap','yea','yes','yet','yew','you','zap','zen','zip','zoo',
  'able','ache','acid','aged','aide','aims','also','area','arms','army','arts','aunt','auto','away','baby','back','bake','ball','band','bank','bare','barn','base','bath','beam','bean','bear','beat','beds','beef','been','beer','bell','belt','bend','bent','best','bike','bill','bird','bite','blew','blow','blue','blur','boat','body','boil','bold','bolt','bomb','bond','bone','book','boom','boot','bore','born','boss','both','bowl','boys','brag','burn','bury','bush','busy','cage','cake','calf','call','calm','came','camp','cane','cape','caps','card','care','cart','case','cash','cast','cats','cave','cell','chat','chef','chin','chip','chop','city','clap','clay','clip','club','clue','coal','coat','code','coil','coin','cold','come','cook','cool','cope','copy','cord','core','corn','cost','coup','crab','crew','crop','crow','cube','cure','curl','cute','damp','dare','dark','dash','data','date','dawn','days','dead','deaf','deal','dear','debt','deck','deed','deem','deep','deer','demo','deny','desk','dial','dice','died','dies','diet','dirt','disc','dish','disk','dive','dock','does','dogs','doll','dome','done','doom','door','dose','down','drag','draw','drew','drip','drop','drum','duck','dude','duel','duke','dull','dumb','dump','dune','dust','duty','each','earl','earn','ears','ease','east','easy','eats','echo','edge','edit','eels','eggs','else','emit','ends','epic','euro','even','ever','evil','exam','exit','face','fact','fade','fail','fair','fake','fall','fame','fang','farm','fast','fate','fear','feat','feed','feel','fees','feet','fell','felt','fern','file','fill','film','find','fine','fire','firm','fish','fist','fits','five','flag','flap','flat','flaw','fled','flew','flip','flow','foam','fold','folk','fond','font','food','fool','foot','ford','fore','fork','form','fort','foul','four','free','frog','from','fuel','full','fund','fury','fuse','gain','game','gang','gate','gave','gaze','gear','gems','gene','gift','girl','give','glad','glow','glue','goal','goat','goes','gold','golf','gone','good','grab','gram','gray','grew','grey','grid','grim','grin','grip','grow','gulf','guns','gust','guys','hack','hail','hair','half','hall','halt','hand','hang','hard','harm','hate','have','hawk','haze','head','heal','heap','hear','heat','heel','held','hell','help','herb','herd','here','hero','hide','high','hike','hill','hint','hire','hits','hold','hole','holy','home','hood','hook','hope','horn','hose','host','hour','huge','hung','hunt','hurt','hype','iced','icon','idea','inch','into','iron','isle','item','jack','jail','jazz','jean','jeep','jobs','john','join','joke','jump','june','junk','jury','just','keen','keep','kept','keys','kick','kids','kill','kind','king','kiss','kite','knee','knew','knit','knob','knot','know','labs','lack','lady','laid','lake','lamb','lamp','land','lane','last','late','lawn','laws','lead','leaf','lean','leap','left','lend','lens','lent','less','liar','lick','lids','life','lift','like','limb','lime','limp','line','link','lion','lips','list','live','load','loaf','loan','lock','logo','lone','long','look','loop','lord','lose','loss','lost','lots','loud','love','luck','lump','lung','made','mail','main','make','male','mall','many','maps','mark','mars','mask','mass','mate','math','maze','meal','mean','meat','meet','melt','memo','menu','mere','mesh','mess','mice','mild','mile','milk','mill','mind','mine','mint','miss','mist','mode','mold','mood','moon','more','moss','most','moth','move','much','muck','muds','mule','must','myth','nail','name','naps','navy','near','neat','neck','need','neon','nerd','nest','nets','news','next','nice','nine','node','none','noon','norm','nose','note','noun','odds','okay','once','only','onto','open','oral','ours','oven','over','pace','pack','page','paid','pain','pair','pale','palm','pans','park','part','pass','past','path','paws','peak','pear','peas','peel','peer','pets','pick','pier','pigs','pile','pill','pine','pink','pipe','pits','plan','play','plea','plod','plot','plow','plug','plum','plus','poem','poet','poke','pole','poll','pond','pool','poor','pope','pork','port','pose','post','pour','pray','prep','prey','prod','prop','pros','pull','pump','punk','pure','push','quit','quiz','race','rack','rage','raid','rail','rain','rake','ramp','rang','rank','rare','rate','rats','rays','read','real','ream','reap','rear','rely','rent','rest','ribs','rice','rich','ride','ring','riot','rise','risk','road','roam','roar','robe','rock','rode','role','roll','roof','room','root','rope','rose','rude','ruin','rule','runs','rush','rust','safe','sage','said','sail','sake','sale','salt','same','sand','sane','sang','sank','save','saws','says','scan','seal','seat','seed','seek','seem','seen','self','sell','semi','send','sent','sept','sets','sewn','shed','shin','ship','shoe','shoo','shop','shot','show','shut','sick','side','sift','sigh','sign','silk','sing','sink','site','sits','size','skin','skip','slam','slap','slid','slim','slip','slot','slow','snap','snow','soak','soap','soar','sock','soda','sofa','soft','soil','sold','sole','solo','some','song','soon','sort','soul','soup','sour','span','spar','spec','sped','spin','spit','spot','star','stay','stem','step','stew','stir','stop','stow','stub','such','suck','suit','sung','sunk','sure','surf','swim','tack','taco','tail','take','tale','talk','tall','tank','tape','taps','task','team','tear','teen','tell','temp','tend','tens','tent','term','test','text','than','that','them','then','they','thin','this','thus','tick','tide','tidy','tied','ties','tile','till','time','tiny','tips','tire','toad','toes','toil','told','toll','tomb','tone','tons','took','tool','tops','tore','torn','toss','tour','town','toys','trap','tray','tree','trim','trio','trip','trod','trot','true','tube','tuna','tune','turf','turn','twin','type','ugly','undo','unit','unto','upon','urge','urns','used','user','uses','vain','vale','vamp','vane','vans','vary','vase','vast','veal','veer','veil','vein','vent','verb','very','vest','veto','view','vile','vine','visa','void','volt','vote','wade','wage','wait','wake','walk','wall','wand','want','ward','warm','warn','warp','wars','wary','wash','wasp','wave','wavy','waxy','ways','weak','wear','webs','weds','weed','week','weep','weld','well','went','wept','were','west','what','when','whim','whip','wide','wife','wild','will','wind','wine','wing','wink','wins','wipe','wire','wise','wish','with','woke','wolf','womb','wont','wood','wool','word','wore','work','worm','worn','wrap','yard','yarn','year','yell','yoga','yolk','your','zeal','zero','zone','zoom','yoke','joke','coke',
  'heave','about','above','abuse','acted','actor','acute','admit','adopt','adult','after','again','agent','agree','ahead','alarm','album','alert','alike','alive','allow','alone','along','alter','among','angel','anger','angle','angry','apart','apple','apply','arena','argue','arise','armor','arose','array','arrow','aside','asset','avoid','await','awake','award','aware','awful','bacon','badge','badly','baker','balls','bands','banks','based','bases','basic','basis','batch','beach','beans','beard','bears','beast','beats','began','begin','begun','being','belly','below','belts','bench','berry','bikes','bills','birds','birth','black','blade','blame','blank','blast','blaze','bleed','blend','bless','blind','blink','block','blogs','blood','blown','blues','blunt','board','boats','bonds','bones','bonus','books','boost','booth','boots','bored','bound','boxes','brain','brake','brand','brass','brave','bread','break','breed','brick','bride','brief','bring','broad','broke','brown','brush','buddy','build','built','bunch','burns','burst','buyer','cabin','cable','calls','camps','canal','candy','cards','cargo','carry','cases','catch','cause','caves','cease','cells','chain','chair','champ','chaos','charm','chart','chase','cheap','cheat','check','cheek','cheer','chess','chest','chief','child','chill','china','chips','chose','chunk','claim','clash','class','clean','clear','clerk','click','cliff','climb','cling','clips','cloak','clock','clone','close','cloth','cloud','clubs','coach','coast','coats','codes','coins','colon','color','comes','comic','coral','costs','couch','could','count','court','cover','crack','craft','crane','crash','crazy','cream','creek','creep','crest','crime','crisp','cross','crowd','crown','cruel','crush','curve','cycle','daddy','daily','dairy','dance','dated','dates','deals','dealt','death','debut','decay','decks','decor','deeds','delay','delta','dense','depot','depth','desks','devil','diary','dirty','disco','ditch','diver','dizzy','dodge','doing','dolls','doors','doubt','dough','dozen','draft','drain','drama','drank','drawn','draws','dream','dress','dried','drift','drill','drink','drive','drops','drove','drown','drugs','drums','drunk','dryer','ducks','dumps','dusty','dutch','dwarf','dwell','dying','eager','eagle','early','earth','eased','eaten','edges','eight','elbow','elder','elect','elite','email','empty','ended','enemy','enjoy','enter','entry','equal','error','essay','event','every','exact','exams','exist','extra','faced','faces','facts','faded','fails','faint','fairy','faith','falls','false','famed','fancy','farms','fatal','fatty','fault','favor','feast','feels','fence','ferry','fetal','fetch','fever','fewer','fiber','field','fiery','fifth','fifty','fight','filed','files','fills','films','final','finds','fired','fires','firms','first','fixed','fixes','flags','flame','flash','flask','flats','flesh','flies','float','flock','flood','floor','flour','flown','flows','fluid','flush','focal','focus','folks','fonts','foods','force','forge','forms','forth','forty','forum','found','frame','frank','fraud','freak','freed','fresh','fried','front','frost','fruit','fuels','fully','funds','funny','gains','games','gangs','gases','gates','gauge','gears','genes','genre','ghost','giant','gifts','girls','given','gives','gland','glass','gleam','glide','globe','glory','gloss','glove','goals','going','goods','goose','grace','grade','grain','grand','grant','grape','graph','grasp','grass','grave','greed','greek','green','greet','grief','grill','grind','grips','gross','group','grove','grown','grows','guard','guess','guest','guide','guild','guilt','guise','habit','hairs','halls','hands','handy','hangs','happy','harsh','hatch','hated','hates','haven','heads','heard','heart','heath','heavy','hedge','heels','hello','helps','herbs','herds','hills','hints','hired','hobby','holds','holes','homes','honey','honor','hooks','hoped','hopes','horns','horse','hosts','hotel','hours','house','human','humor','hurry','hurts','icons','ideal','ideas','image','imply','inbox','index','indie','inner','input','irony','issue','items','ivory','jacks','japan','jeans','jewel','joint','joker','jokes','jolly','jones','judge','juice','jumps','keeps','kills','kinds','kings','knees','knife','knock','knots','known','knows','label','labor','lacks','lakes','lamps','lance','lands','lanes','large','laser','lasts','later','laugh','layer','leads','leaks','leaps','learn','lease','least','leave','legal','lemon','level','lever','light','liked','likes','limbs','limit','lined','linen','liner','lines','links','lions','lists','lived','liver','lives','loads','loans','lobby','local','locks','lodge','logic','logos','loose','lords','loser','loses','lotus','loved','lover','loves','lower','loyal','lucky','lunch','lungs','lying','lyric','macro','magic','major','maker','makes','males','malls','manga','manor','maple','march','marks','marsh','masks','mason','match','maybe','mayor','meals','means','meant','meats','medal','media','meets','melon','memos','menus','mercy','merge','merit','merry','messy','metal','meter','metro','micro','midst','might','miles','mills','minds','miner','mines','minor','minus','misty','mixed','mixer','model','modem','modes','moist','molds','mommy','money','month','moods','moral','motor','mount','mouse','mouth','moved','moves','movie','muddy','multi','music','myths','nails','naked','named','names','nasty','naval','needs','nerve','never','newer','newly','night','noble','nodes','noise','noisy','norms','north','notch','noted','notes','novel','nurse','occur','ocean','offer','often','olive','onset','opens','opera','orbit','order','organ','other','ought','outer','owned','owner','oxide','ozone','packs','pages','pains','paint','pairs','palms','panel','panic','pants','paper','parks','parts','party','pasta','paste','patch','paths','pause','peace','peach','peaks','pearl','pedal','peers','penny','perks','pests','petty','phase','phone','photo','piano','picks','piece','piles','pills','pilot','pinch','pipes','pitch','pizza','place','plain','plane','plans','plant','plate','plays','plaza','pleas','plots','pluck','plumb','poems','point','poise','poker','polar','polls','ponds','pools','porch','pores','ports','posed','poses','posts','pouch','pound','power','press','price','pride','prime','print','prior','probe','promo','prone','proof','props','proud','prove','proxy','pulse','pumps','punch','pupil','puppy','purse','queen','query','quest','quick','quiet','quilt','quite','quote','races','racks','radar','radio','raids','rails','rains','rainy','raise','rally','ranch','range','ranks','rapid','rated','rates','ratio','razor','reach','react','reads','ready','realm','rebel','refer','reign','relax','relay','relic','remit','remix','renal','renew','repay','reply','reset','resin','retro','rider','rides','ridge','rifle','right','rigid','rings','rinse','riots','risen','rises','risks','risky','rival','river','roads','roast','robot','rocks','rocky','rogue','roles','roman','rooms','roots','ropes','roses','rough','round','route','royal','ruins','ruled','ruler','rules','rural','sadly','safer','saint','salad','sales','salon','salsa','sandy','sauce','saved','saves','scale','scalp','scams','scans','scare','scarf','scary','scene','scent','scope','score','scout','scrap','seals','seats','seeds','seeks','seems','seize','sells','sends','sense','serum','serve','setup','seven','shade','shady','shaft','shake','shaky','shall','shame','shape','share','shark','sharp','shear','sheds','sheer','sheet','shelf','shell','shift','shine','shiny','ships','shirt','shock','shoes','shook','shoot','shops','shore','short','shots','shown','shows','sides','siege','sight','sigma','signs','silly','since','sites','sixth','sixty','sized','sizes','skies','skill','skins','skirt','skull','slash','slate','slave','sleek','sleep','slept','slice','slide','slope','slots','small','smart','smell','smile','smoke','smoky','snake','snaps','sneak','sniff','solar','solid','solve','songs','sonic','sorry','sorts','souls','sound','south','space','spare','spark','spawn','speak','spear','specs','speed','spell','spend','spent','spice','spicy','spill','spine','split','spoke','spoon','sport','spots','spray','squad','stack','staff','stage','stain','stake','stall','stamp','stand','stare','stark','stars','start','state','stays','steak','steal','steam','steel','steep','steer','stems','steps','stick','stiff','still','stock','stomp','stone','stood','stool','stops','store','storm','story','stove','strap','straw','strip','stuck','study','stuff','style','sucks','sugar','suite','suits','sunny','super','surge','swamp','swear','sweat','sweep','sweet','swept','swift','swing','swiss','sword','sworn','syrup','table','tails','taken','takes','tales','talks','tanks','tapes','taste','tasty','taxes','teach','teams','tears','teeth','tells','tempo','tends','tenor','tense','tenth','terms','tests','texts','thank','theft','theme','thick','thief','thigh','thing','think','third','threw','throw','thumb','tiger','tight','tiles','timer','times','tired','tires','title','toast','today','token','tombs','tones','tools','tooth','topic','torch','total','touch','tough','tours','towel','tower','towns','trace','track','tract','trade','trail','train','trait','trash','treat','trees','trend','trial','tribe','trick','tried','tries','trips','troll','troop','truck','truly','trunk','trust','truth','tubes','tumor','tunes','tuned','turns','tutor','twice','twins','twist','types','ultra','uncle','under','undue','union','units','unity','until','upper','upset','urban','urged','usage','users','using','usual','utter','vague','valid','value','valve','vault','vegas','veins','venue','venus','verge','verse','video','views','villa','vinyl','viral','virus','visit','vital','vivid','vocal','vodka','vogue','voice','voted','voter','votes','wages','wagon','waist','walks','walls','wants','warms','warns','waste','watch','water','waves','wears','weary','wedge','weeds','weeks','weigh','weird','wells','whale','wheat','wheel','where','which','while','white','whole','whose','widen','wider','width','winds','wings','wiped','wires','witch','woman','women','words','works','world','worms','worry','worse','worst','worth','would','wound','wrath','wreck','wrist','write','wrong','wrote','yacht','yards','years','yeast','yield','young','yours','youth','zones','poked','joked','woken','token','stoke','evoke','choke','bloke','spoke','lemon','melon','moldy','loamy','motel','model',
  // British English spellings
  'colour','favour','flavour','honour','humour','labour','neighbour','rumour','savour','vapour','behaviour','harbour','armour','parlour','tumour','valour','candour','clamour','demeanour','endeavour','fervour','glamour','rigour','rancour','splendour','vigour','centre','fibre','litre','metre','theatre','spectre','sombre','lustre','meagre','sabre','calibre','manoeuvre','defence','offence','licence','pretence','practise','analyse','paralyse','catalyse','organised','realised','recognised','criticised','emphasised','summarised','apologised','specialised','characterised','minimised','maximised','prioritised','standardised','customised','optimised','visualised','utilised','stabilised','neutralised','legalised','localised','globalised','nationalised','rationalised','personalised','generalised','normalised','formalised','centralised','decentralised','materialised','commercialised','industrialised','hospitalised','criminalised','marginalised','capitalised','socialised','privatised','finalised','equalised','idealised','moralised','vitalised','brutalised','penalised','signalised','legalise','localise','globalise','nationalise','rationalise','personalise','generalise','normalise','formalise','centralise','decentralise','materialise','commercialise','industrialise','hospitalise','criminalise','marginalise','capitalise','socialise','privatise','finalise','equalise','idealise','moralise','vitalise','brutalise','penalise','signalise','travelled','travelling','traveller','cancelled','cancelling','labelled','labelling','modelled','modelling','levelled','levelling','fuelled','fuelling','jewelled','panelled','panelling','counselled','counselling','channelled','channelling','signalled','signalling','dialled','dialling','equalled','equalling','initialled','initialling','parcelled','parcelling','quarrelled','quarrelling','marvelled','marvelling','rivalled','rivalling','bevelled','bevelling','chiselled','chiselling','cudgelled','cudgelling','duelled','duelling','gruelled','gruelling','imperilled','imperilling','kennelled','kennelling','laurelled','marshalled','marshalling','medalled','medalling','misspelled','misspelling','modelled','nickelled','panelled','pedalled','pedalling','pencilled','pencilling','pummelled','pummelling','rappelled','rappelling','rebelled','rebelling','refuelled','refuelling','revelled','revelling','shovelled','shovelling','snivelled','snivelling','spiralled','spiralling','stencilled','stencilling','swivelled','swivelling','tasselled','towelled','towelling','tramelled','tramelling','trowelled','trowelling','tunnelled','tunnelling','unravelled','unravelling','weaselled','yodelled','yodelling','grey','plough','cheque','catalogue','dialogue','monologue','prologue','analogue','tonne','programme','draught','mould','moult','smoulder','jewellery','pyjamas','aluminium','speciality','tranquillity','fulfil','instil','distil','enrol','skilful','wilful','colourful','flavourful','honourable','favourable','humorous','laborious','rigorous','vigorous','glamorous','clamorous','odorous','rancorous','dolorous','amorous','decorous','sonorous','porous','serous','onerous','generous','dangerous','murderous','treacherous','boisterous','obstreperous','vociferous','pestiferous','odoriferous','coniferous','aquiferous','carboniferous','somniferous','splendiferous','auriferous','floriferous','sudoriferous','proliferous','igniferous','luciferous','melliferous','metalliferous','fossiliferous','graminiferous','umbriferous','umbelliferous','nectariferous','papilliferous','celluliferous','crystalliferous','faeces','foetus','anaemia','diarrhoea','oestrogen','paediatric','haemorrhage','encyclopaedia','mediaeval','aeroplane','gaol','kerb','tyre','sceptic','moustache','storey','cosy','ageing','judgement','acknowledgement','abridgement','lodgement','routeing','hoeing','toeing','canoeing','queueing','singeing','dyeing','eyeing','stymied','taxiing'
]);

const validateWord = async (word) => {
  if (WORDS.has(word.toLowerCase())) return true;
  if (word.length >= 4) {
    try {
      const res = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${word.toLowerCase()}`);
      return res.ok;
    } catch { return false; }
  }
  return false;
};

const generateLetters = (count = 3) => {
  const vowels = 'AEIOU', consonants = 'BCDFGHJKLMNPQRSTVWXYZ';
  const letters = [vowels[Math.floor(Math.random() * vowels.length)]];
  // Add a second vowel if generating 5 letters
  if (count >= 5) {
    let v2 = vowels[Math.floor(Math.random() * vowels.length)];
    while (v2 === letters[0]) v2 = vowels[Math.floor(Math.random() * vowels.length)];
    letters.push(v2);
  }
  while (letters.length < count) {
    const c = consonants[Math.floor(Math.random() * consonants.length)];
    if (!letters.includes(c)) letters.push(c);
  }
  return letters.sort(() => Math.random() - 0.5);
};

// Board: 20 wide x 10 high = 200 squares
const BOARD_SIZE = 200;
const BOARD_COLS = 20;
const BOARD_ROWS = 10;
const TURN_TIME = 30;

const STAIRS = [
  { from: 7, to: 34 }, { from: 21, to: 42 }, { from: 38, to: 84 },
  { from: 56, to: 78 }, { from: 91, to: 118 }, { from: 103, to: 124 },
  { from: 134, to: 159 }, { from: 148, to: 167 }, { from: 172, to: 191 },
];

const SLIDES = [
  { from: 25, to: 6 }, { from: 47, to: 19 }, { from: 63, to: 41 },
  { from: 89, to: 52 }, { from: 99, to: 12 }, { from: 121, to: 98 },
  { from: 144, to: 109 }, { from: 166, to: 137 }, { from: 183, to: 158 },
  { from: 195, to: 163 },
];

// Updated abilities with clearer descriptions
const ABILITIES = [
  { id: 'extra_time', name: 'Extra Time', icon: '⏰', desc: 'You get +10 seconds this turn', color: '#3B82F6' },
  { id: 'scramble', name: 'Scramble', icon: '🔀', desc: 'Get new key letters', color: '#8B5CF6' },
  { id: 'hint', name: 'Hint', icon: '💡', desc: 'Shows a word suggestion', color: '#F59E0B' },
  { id: 'skip_next', name: 'Skip', icon: '⏭️', desc: 'Next player misses their turn', color: '#EF4444' },
  { id: 'double_points', name: 'Double', icon: '✨', desc: 'Your points are doubled', color: '#10B981' },
  { id: 'double_turn', name: 'Again', icon: '🔄', desc: 'You get another turn after this', color: '#EC4899' },
  { id: 'extra_keys', name: '5 Key Letters', icon: '🔑', desc: 'Get 5 key letters for 5x multiplier!', color: '#F97316' },
];

const WHEEL_SEGMENTS = [];
for (let i = 0; i < 7; i++) {
  WHEEL_SEGMENTS.push(ABILITIES[i]);
  WHEEL_SEGMENTS.push({ id: 'none', name: 'No Ability', icon: '❌', desc: 'No ability this spin, better luck on your next turn!', color: i % 2 === 0 ? '#4B5563' : '#374151' });
}

const CHARACTERS = [
  { id: 1, name: 'Red Ranger', color: '#EF4444', emoji: null, svg: '2.svg' },
  { id: 2, name: 'Blue Wizard', color: '#3B82F6', emoji: null, svg: '4.svg' },
  { id: 3, name: 'Green Knight', color: '#10B981', emoji: null, svg: '1.svg' },
  { id: 4, name: 'Purple Magic', color: '#8B5CF6', emoji: null, svg: '5.svg' },
  { id: 5, name: 'Yellow Dream', color: '#FBBF24', emoji: null, svg: '3.svg' },
  { id: 6, name: 'Grey Trickster', color: '#6B7280', emoji: null, svg: '6.svg' },
];

// Smart hint - finds best word using all key letters
const getHintWord = (letters) => {
  const lettersLower = letters.map(l => l.toLowerCase());
  const targetCount = letters.length;
  
  // First try to find a word with all letters
  let bestWords = [];
  for (const word of WORDS) {
    if (word.length >= 4) {
      const wordLower = word.toLowerCase();
      let matchCount = 0;
      const used = [];
      for (const letter of lettersLower) {
        if (wordLower.includes(letter) && !used.includes(letter)) {
          matchCount++;
          used.push(letter);
        }
      }
      if (matchCount === targetCount) {
        bestWords.push(word);
      }
    }
  }
  
  // If found words with all letters, return longest one
  if (bestWords.length > 0) {
    bestWords.sort((a, b) => b.length - a.length);
    return bestWords[0];
  }
  
  // Try words with targetCount - 1 letters
  for (const word of WORDS) {
    if (word.length >= 4) {
      const wordLower = word.toLowerCase();
      let matchCount = 0;
      const used = [];
      for (const letter of lettersLower) {
        if (wordLower.includes(letter) && !used.includes(letter)) {
          matchCount++;
          used.push(letter);
        }
      }
      if (matchCount >= targetCount - 1) {
        bestWords.push(word);
      }
    }
  }
  
  if (bestWords.length > 0) {
    bestWords.sort((a, b) => b.length - a.length);
    return bestWords[0];
  }
  
  // Fallback
  return 'think';
};

// Rules component
const Rules = ({ onClose }) => (
  <div className="rules-overlay">
    <div className="rules-content">
      <h2>📖 How to Play</h2>
      
      <div className="rule-section">
        <h3>🎯 Goal</h3>
        <p>Be the first player to reach <strong>square 200</strong>!</p>
      </div>
      
      <div className="rule-section">
        <h3>🎮 How To Play</h3>
        <ol>
          <li><strong>Spin the wheel</strong> - You might get a special ability!</li>
          <li><strong>You're given 3 key letters</strong> - type any valid word in the dictionary to score points using at least 1 key letter.</li>
          <li><strong>You have 30 seconds</strong> to type a word.</li>
          <li><strong>Each letter scores 1 point.</strong></li>
          <li><strong>If you don't use a key letter</strong>, you'll only move a maximum of 1 space - even with a valid word.</li>
          <li><strong>Using key letters multiplies your score.</strong></li>
          <li><strong>Your score = the number of spaces you move forwards.</strong></li>
          <li><strong>Watch out for green stairs and red slides!</strong></li>
          <li><strong>If you fail to submit a word, you move 0 spaces.</strong></li>
        </ol>
      </div>
      
      <div className="rule-section highlight-section">
        <h3>🔑 KEY LETTERS = MORE MOVES!</h3>
        
        <div className="scoring-box">
          <div className="scoring-formula">
            <span className="formula-part">Word Length</span>
            <span className="formula-x">×</span>
            <span className="formula-part">Key Letters Used</span>
            <span className="formula-eq">=</span>
            <span className="formula-part result">Your Score</span>
          </div>
        </div>
        
        <div className="example-box">
          <div className="example-row bad">
            <span className="ex-calc">5 letters × 0 key letters = <strong>1 space</strong></span>
          </div>
          <div className="example-row ok">
            <span className="ex-calc">5 letters × 2 keys = <strong>10 spaces</strong></span>
          </div>
          <div className="example-row great">
            <span className="ex-calc">6 letters × 3 keys = <strong>18 spaces</strong></span>
          </div>
        </div>
      </div>
      
      <div className="rule-section">
        <h3>🎡 Wheel Abilities</h3>
        <ul>
          <li>⏰ <strong>Extra Time</strong> - +10 seconds this turn</li>
          <li>🔀 <strong>Scramble</strong> - Get new key letters</li>
          <li>💡 <strong>Hint</strong> - Shows the best word possible</li>
          <li>⏭️ <strong>Skip</strong> - Next player misses their turn</li>
          <li>✨ <strong>Double</strong> - Your score is doubled</li>
          <li>🔄 <strong>Again</strong> - You get another turn</li>
          <li>🔑 <strong>5 Key Letters</strong> - Get 5 key letters for up to 5x multiplier!</li>
          <li>❌ <strong>Nothing</strong> - No ability this turn</li>
        </ul>
      </div>
      
      <button className="close-rules-btn" onClick={onClose}>Got it! ✓</button>
    </div>
  </div>
);

export default function WordSlide() {
  const [gamePhase, setGamePhase] = useState('setup');
  const [showRules, setShowRules] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [players, setPlayers] = useState([]);
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [availableCharacters, setAvailableCharacters] = useState([...CHARACTERS]);
  const [playerNames, setPlayerNames] = useState(['', '']);
  const [selectingPlayerIndex, setSelectingPlayerIndex] = useState(0);
  const [letters, setLetters] = useState([]);
  const [currentWord, setCurrentWord] = useState('');
  const [timeLeft, setTimeLeft] = useState(TURN_TIME);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [turnPhase, setTurnPhase] = useState('intro');
  const [currentAbility, setCurrentAbility] = useState(null);
  const [wordResult, setWordResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [skipNextPlayer, setSkipNextPlayer] = useState(false);
  const [doubleTurn, setDoubleTurn] = useState(false);
  const [hintWord, setHintWord] = useState(null);
  const [wheelRotation, setWheelRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [landedSegment, setLandedSegment] = useState(null);
  const [animatingPlayer, setAnimatingPlayer] = useState(null);
  const [animationPath, setAnimationPath] = useState([]);
  const [animationPosition, setAnimationPosition] = useState(0);
  const [specialAnimation, setSpecialAnimation] = useState(null); // 'stair-pulse', 'slide-pulse', or null
  const [winner, setWinner] = useState(null);
  const [boardView, setBoardView] = useState('overview'); // 'overview', 'zoomed', 'following'
  const [zoomTarget, setZoomTarget] = useState(null); // position to zoom to
  const timerRef = useRef(null);
  const wheelRef = useRef(null);
  const boardRef = useRef(null);

  const addPlayerSlot = () => { if (playerNames.length < 6) setPlayerNames([...playerNames, '']); };
  const removePlayerSlot = (index) => { if (playerNames.length > 2) setPlayerNames(playerNames.filter((_, i) => i !== index)); };
  const updatePlayerName = (index, name) => { const n = [...playerNames]; n[index] = name; setPlayerNames(n); };

  // Start anticipation music when in setup
  useEffect(() => {
    if (gamePhase === 'setup' || gamePhase === 'character-select') {
      if (!SoundManager.musicPlaying || SoundManager.musicType !== 'anticipation') {
        SoundManager.stopMusic();
        SoundManager.startMusic('anticipation');
      }
    }
  }, [gamePhase]);

  const startCharacterSelect = () => {
    const validNames = playerNames.filter(name => name.trim());
    if (validNames.length >= 2) {
      setPlayers(validNames.map((name, i) => ({ id: i, name: name.trim(), character: null, position: 0, score: 0 })));
      setAvailableCharacters([...CHARACTERS]);
      setGamePhase('character-select');
    }
  };

  const selectCharacter = (character) => {
    const updatedPlayers = [...players];
    updatedPlayers[selectingPlayerIndex].character = character;
    setPlayers(updatedPlayers);
    setAvailableCharacters(availableCharacters.filter(c => c.id !== character.id));
    if (selectingPlayerIndex < players.length - 1) setSelectingPlayerIndex(selectingPlayerIndex + 1);
    else { 
      setGamePhase('playing'); 
      setTurnPhase('intro'); 
      SoundManager.stopMusic();
      SoundManager.startMusic('game'); 
    }
  };

  const startNewGame = () => {
    SoundManager.stopMusic();
    GameCounter.reset(); // Reset spin counter for new game
    setGamePhase('setup'); setPlayers([]); setPlayerNames(['', '']);
    setCurrentPlayerIndex(0); setWinner(null); setSelectingPlayerIndex(0);
    setWheelRotation(0); setCurrentAbility(null); setTurnPhase('intro');
    setAnimatingPlayer(null); setDoubleTurn(false); setSkipNextPlayer(false);
    setLandedSegment(null); setBoardView('overview'); setZoomTarget(null);
    SoundManager.startMusic('anticipation');
  };

  // Wheel spin - spin first, then determine result from where it lands
  const spinWheel = () => {
    if (isSpinning) return;
    
    setIsSpinning(true);
    SoundManager.play('wheelSpin');
    GameCounter.addSpin(); // Track spin for game counter
    
    // Spin a random amount (5-8 full rotations plus random extra)
    const fullSpins = 5 + Math.floor(Math.random() * 3);
    const extraDegrees = Math.random() * 360;
    const totalRotation = fullSpins * 360 + extraDegrees;
    const newRotation = wheelRotation + totalRotation;
    
    setWheelRotation(newRotation);
    
    // After animation, calculate which segment the pointer landed on
    setTimeout(() => {
      // Normalize the final rotation to 0-360
      const normalizedRotation = newRotation % 360;
      // The pointer is at top (0 degrees). Wheel rotates clockwise.
      // 14 segments = 360/14 ≈ 25.71 degrees per segment
      const segmentDegrees = 360 / 14;
      const adjustedAngle = (360 - normalizedRotation + 360) % 360;
      const segmentIndex = Math.floor(adjustedAngle / segmentDegrees) % 14;
      
      const resultAbility = WHEEL_SEGMENTS[segmentIndex];
      setLandedSegment(segmentIndex);
      setCurrentAbility(resultAbility);
      setIsSpinning(false);
      setTurnPhase('ability-reveal');
      SoundManager.play('wheelLand');
    }, 4000);
  };

  const showReady = () => { setTurnPhase('ready'); setTimeout(() => startTurn(), 1500); };

  const startTurn = () => {
    const letterCount = currentAbility?.id === 'extra_keys' ? 5 : 3;
    setLetters(generateLetters(letterCount)); setCurrentWord(''); setWordResult(null); setHintWord(null);
    setTimeLeft(currentAbility?.id === 'extra_time' ? TURN_TIME + 10 : TURN_TIME);
    setTurnPhase('playing'); setIsTimerRunning(true);
  };

  useEffect(() => {
    if (isTimerRunning && timeLeft > 0) {
      timerRef.current = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    } else if (isTimerRunning && timeLeft === 0) {
      setIsTimerRunning(false);
      if (currentWord.length >= 3) {
        validateWord(currentWord).then(isValid => {
          if (isValid) {
            const wordUpper = currentWord.toUpperCase();
            let matchCount = 0;
            const usedLetters = [];
            for (const letter of letters) {
              if (wordUpper.includes(letter) && !usedLetters.includes(letter)) { matchCount++; usedLetters.push(letter); }
            }
            let multiplier = matchCount || 0;
            if (currentAbility?.id === 'double_points') multiplier *= 2;
            const points = matchCount === 0 ? 1 : currentWord.length * multiplier;
            setWordResult({ valid: true, word: currentWord, points, multiplier, matchCount, wordLength: currentWord.length });
          } else {
            setWordResult({ valid: false, word: currentWord, points: 0 });
          }
          setTurnPhase('result');
        });
      } else {
        setWordResult({ valid: false, word: currentWord || '(none)', points: 0, timeOut: true });
        setTurnPhase('result');
        SoundManager.play('timeout');
      }
    }
    return () => clearTimeout(timerRef.current);
  }, [isTimerRunning, timeLeft, currentWord, letters, currentAbility]);

  const calculateScore = (word, lettersArr) => {
    const wordUpper = word.toUpperCase();
    let matchCount = 0;
    const usedLetters = [];
    for (const letter of lettersArr) {
      if (wordUpper.includes(letter) && !usedLetters.includes(letter)) { matchCount++; usedLetters.push(letter); }
    }
    if (matchCount === 0) return { points: 1, multiplier: 0, matchCount: 0 };
    let multiplier = matchCount;
    if (currentAbility?.id === 'double_points') multiplier *= 2;
    return { points: word.length * multiplier, multiplier, matchCount };
  };

  const submitWord = useCallback(async () => {
    if (currentWord.length < 3 || isValidating) return;
    setIsValidating(true);
    setIsTimerRunning(false);
    const isValid = await validateWord(currentWord);
    if (isValid) {
      const { points, multiplier, matchCount } = calculateScore(currentWord, letters);
      setWordResult({ valid: true, word: currentWord, points, multiplier, matchCount, wordLength: currentWord.length });
      SoundManager.play('submit');
    } else {
      setWordResult({ valid: false, word: currentWord, points: 0 });
      SoundManager.play('invalid');
    }
    setIsValidating(false);
    setTurnPhase('result');
  }, [currentWord, isValidating, letters, currentAbility]);

  const buildAnimationPath = (startPos, points) => {
    const path = [];
    let pos = startPos;
    // Only add steps until we reach BOARD_SIZE
    for (let i = 0; i < points; i++) { 
      pos++; 
      if (pos >= BOARD_SIZE) { 
        path.push({ position: BOARD_SIZE, type: 'move' }); 
        break; // Stop - we've reached the end!
      }
      path.push({ position: pos, type: 'move' }); 
    }
    // Only check for stairs/slides if we haven't won
    if (pos < BOARD_SIZE) {
      const stair = STAIRS.find(s => s.from === pos);
      if (stair) { for (let i = pos + 1; i <= stair.to; i++) path.push({ position: i, type: 'stair' }); }
      const slide = SLIDES.find(s => s.from === pos);
      if (slide) { for (let i = pos - 1; i >= slide.to; i--) path.push({ position: i, type: 'slide' }); }
    }
    return path;
  };

  const applyMove = () => {
    const points = wordResult?.points || 1;
    const path = buildAnimationPath(players[currentPlayerIndex].position, points);
    setAnimatingPlayer(currentPlayerIndex);
    setAnimationPath(path);
    setAnimationPosition(0);
    // Start zoomed to current position, then follow
    setBoardView('following');
    setZoomTarget(players[currentPlayerIndex].position);
    setTurnPhase('animating');
  };

  // Track whether we've shown the pulse for the current special step
  const [pulseShown, setPulseShown] = useState(false);

  useEffect(() => {
    if (turnPhase !== 'animating' || animationPath.length === 0) return;
    
    if (animationPosition < animationPath.length) {
      const step = animationPath[animationPosition];
      const prevStep = animationPosition > 0 ? animationPath[animationPosition - 1] : null;
      
      // Update zoom target to follow the player
      setZoomTarget(step.position);
      
      // Check if we're about to start stair/slide movement (first stair/slide step after regular moves)
      const isFirstSpecialStep = prevStep?.type === 'move' && (step.type === 'stair' || step.type === 'slide');
      
      // Only trigger pulse if this is the first special step AND we haven't shown the pulse yet
      if (isFirstSpecialStep && !specialAnimation && !pulseShown) {
        // Mark that we've shown the pulse for this special step
        setPulseShown(true);
        // Start the pulse animation
        setSpecialAnimation(step.type === 'stair' ? 'stair-pulse' : 'slide-pulse');
        // Play stair or slide sound
        SoundManager.play(step.type === 'stair' ? 'stair' : 'slide');
        // After 1 second, clear animation and continue with movement
        setTimeout(() => {
          setSpecialAnimation(null);
        }, 1000);
        return;
      }
      
      // If pulse animation is active, wait for it to finish
      if (specialAnimation) {
        return;
      }
      
      // Determine animation speed - faster for stairs/slides
      const speed = (step.type === 'stair' || step.type === 'slide') ? 100 : 400;
      
      const timer = setTimeout(() => {
        const updatedPlayers = [...players];
        updatedPlayers[animatingPlayer].position = step.position;
        setPlayers(updatedPlayers);
        setAnimationPosition(animationPosition + 1);
        // Play move sound for regular moves
        if (step.type === 'move') SoundManager.play('move');
      }, speed);
      return () => clearTimeout(timer);
    } else {
      // Animation complete - zoom out smoothly
      const finalPosition = animationPath[animationPath.length - 1]?.position || players[animatingPlayer].position;
      const updatedPlayers = [...players];
      updatedPlayers[animatingPlayer].score += (wordResult?.points || 1);
      setPlayers(updatedPlayers);
      setAnimatingPlayer(null);
      setAnimationPath([]);
      setSpecialAnimation(null);
      setPulseShown(false);
      
      // Smooth zoom out to overview
      setBoardView('overview');
      setZoomTarget(null);
      
      if (finalPosition >= BOARD_SIZE) {
        setWinner(players[currentPlayerIndex]);
        setGamePhase('game-over');
        SoundManager.stopMusic();
        SoundManager.play('win');
        return;
      }
      
      if (currentAbility?.id === 'skip_next') setSkipNextPlayer(true);
      if (currentAbility?.id === 'double_turn' && !doubleTurn) {
        setDoubleTurn(true);
        setCurrentAbility(null);
      }
      setTurnPhase('board-view');
    }
  }, [turnPhase, animationPosition, animationPath, specialAnimation, pulseShown]);

  const proceedToNextTurn = () => {
    if (doubleTurn) {
      setDoubleTurn(false);
      setTurnPhase('intro');
      return;
    }
    nextTurn();
  };

  const nextTurn = () => {
    setDoubleTurn(false);
    let nextIndex = (currentPlayerIndex + 1) % players.length;
    if (skipNextPlayer) { nextIndex = (nextIndex + 1) % players.length; setSkipNextPlayer(false); }
    setCurrentPlayerIndex(nextIndex);
    setCurrentAbility(null);
    setTurnPhase('intro');
    setCurrentWord('');
    setWordResult(null);
    setLandedSegment(null);
  };

  const handleKeyPress = useCallback((key) => {
    if (turnPhase !== 'playing' || !isTimerRunning) return;
    SoundManager.play('keyPress');
    if (key === 'BACKSPACE') setCurrentWord(prev => prev.slice(0, -1));
    else if (key === 'ENTER') { if (currentWord.length >= 3) submitWord(); }
    else if (/^[A-Z]$/.test(key)) setCurrentWord(prev => prev.length < 15 ? prev + key : prev);
  }, [turnPhase, isTimerRunning, currentWord, submitWord]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (turnPhase !== 'playing') return;
      if (e.key === 'Backspace') handleKeyPress('BACKSPACE');
      else if (e.key === 'Enter') handleKeyPress('ENTER');
      else if (/^[a-zA-Z]$/.test(e.key)) handleKeyPress(e.key.toUpperCase());
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [turnPhase, handleKeyPress]);

  const useScramble = () => { if (currentAbility?.id === 'scramble' && !currentAbility.used) { setLetters(generateLetters(letters.length)); setCurrentAbility({ ...currentAbility, used: true }); } };
  const useHint = () => { if (currentAbility?.id === 'hint' && !hintWord) { setHintWord(getHintWord(letters)); setCurrentAbility({ ...currentAbility, used: true }); } };

  // Calculate board transform for zoom/pan
  const getBoardTransform = () => {
    if (boardView === 'overview' || !zoomTarget) {
      return { transform: 'scale(1) translate(0, 0)', transformOrigin: 'center center' };
    }
    
    // Calculate row and column from position
    const row = Math.floor((zoomTarget - 1) / BOARD_COLS);
    const col = (zoomTarget - 1) % BOARD_COLS;
    // Adjust for snake pattern
    const actualCol = row % 2 === 1 ? (BOARD_COLS - 1 - col) : col;
    // Convert to percentage (invert row because board is rendered bottom-to-top)
    const invertedRow = BOARD_ROWS - 1 - row;
    const xPercent = ((actualCol + 0.5) / BOARD_COLS) * 100;
    const yPercent = ((invertedRow + 0.5) / BOARD_ROWS) * 100;
    
    // Calculate translation to center the target
    const translateX = 50 - xPercent;
    const translateY = 50 - yPercent;
    
    return {
      transform: `scale(2.5) translate(${translateX}%, ${translateY}%)`,
      transformOrigin: 'center center'
    };
  };

  const renderBoard = () => {
    const squares = [];
    
    for (let row = BOARD_ROWS - 1; row >= 0; row--) {
      for (let c = 0; c < BOARD_COLS; c++) {
        const col = row % 2 === 1 ? (BOARD_COLS - 1 - c) : c;
        const displayNum = row * BOARD_COLS + col + 1;
        
        if (displayNum > BOARD_SIZE) continue;
        
        const stair = STAIRS.find(s => s.from === displayNum);
        const slide = SLIDES.find(s => s.from === displayNum);
        const playersHere = players.filter(p => p.position === displayNum);
        
        let cls = 'sq';
        let specialText = null;
        if (stair) { cls += ' stair'; specialText = `+${stair.to - stair.from}`; }
        if (slide) { cls += ' slide'; specialText = `${slide.to - slide.from}`; }
        if (displayNum === BOARD_SIZE) cls += ' finish';
        if (displayNum === 1) cls += ' start';
        
        squares.push(
          <div key={displayNum} className={cls}>
            <span className="sq-num">{displayNum}</span>
            {specialText && <span className="special">{specialText}</span>}
            {playersHere.length > 0 && (
              <div className="tokens">
                {playersHere.map((p, idx) => {
                  // Check if this player is animating and has a pulse animation
                  const isAnimatingWithPulse = animatingPlayer === p.id && specialAnimation;
                  const pulseClass = isAnimatingWithPulse ? specialAnimation : '';
                  
                  return (
                    <span 
                      key={p.id} 
                      className={`token ${pulseClass}`}
                      style={{ 
                        background: p.character?.color,
                        transform: playersHere.length > 1 
                          ? `translate(calc(-50% + ${(idx - (playersHere.length-1)/2) * 14}px), calc(-50% + ${(idx - (playersHere.length-1)/2) * -10}px))` 
                          : 'translate(-50%, -50%)',
                        left: '50%',
                        top: '50%',
                        zIndex: 10 - idx
                      }}
                    >
                      <CharacterAvatar character={p.character} size="tiny" />
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        );
      }
    }
    return squares;
  };

  const currentPlayer = players[currentPlayerIndex];

  return (
    <div className="game">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&family=Space+Grotesk:wght@500;600;700&display=swap');
        
        :root {
          --bg-deep:#080810;--bg-primary:#0C0C18;--bg-secondary:#141424;--bg-elevated:#1C1C30;--bg-card:rgba(255,255,255,0.03);
          --accent-primary:#6366F1;--accent-secondary:#8B5CF6;--accent-success:#10B981;--accent-warning:#F59E0B;--accent-danger:#EF4444;--accent-pink:#EC4899;
          --text-primary:#F1F5F9;--text-secondary:#94A3B8;--text-muted:#64748B;
          --gradient-rainbow:linear-gradient(135deg,#FF6B6B 0%,#FECA57 25%,#48DBFB 50%,#FF9FF3 75%,#54A0FF 100%);
          --gradient-purple:linear-gradient(135deg,#6366F1,#8B5CF6);--gradient-green:linear-gradient(135deg,#10B981,#059669);--gradient-orange:linear-gradient(135deg,#F97316,#FB923C);
          --radius-sm:8px;--radius-md:12px;--radius-lg:16px;--radius-xl:24px;
        }
        *{box-sizing:border-box;margin:0;padding:0}
        html,body{overflow:hidden;height:100%}
        .game{height:100vh;height:100dvh;background:linear-gradient(180deg,var(--bg-deep) 0%,var(--bg-primary) 40%,var(--bg-secondary) 100%);font-family:'Outfit',-apple-system,sans-serif;color:var(--text-primary);overflow:hidden;display:flex;flex-direction:column;position:relative;padding-left:env(safe-area-inset-left);padding-right:env(safe-area-inset-right)}
        .game::before{content:'';position:absolute;top:0;left:0;right:0;height:60%;background:radial-gradient(ellipse at 50% 0%,rgba(99,102,241,0.08) 0%,transparent 70%);pointer-events:none}
        
        .setup,.char-select,.game-over{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:1.5rem;position:relative;z-index:1}
        .title{font-family:'Outfit',sans-serif;font-weight:900;font-size:clamp(3rem,12vw,5rem);background:var(--gradient-rainbow);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;letter-spacing:-0.02em;margin-bottom:0.25rem;filter:drop-shadow(0 4px 30px rgba(255,107,107,0.3))}
        .subtitle{font-family:'Space Grotesk',sans-serif;font-size:0.85rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.35em;margin-bottom:2.5rem}
        .form{background:rgba(255,255,255,0.02);backdrop-filter:blur(20px);border:1px solid rgba(255,255,255,0.08);border-radius:var(--radius-xl);padding:2rem;width:100%;max-width:420px;box-shadow:0 20px 60px rgba(0,0,0,0.4)}
        .form h2{text-align:center;font-weight:700;font-size:1.2rem;color:var(--text-primary);margin-bottom:1.75rem;letter-spacing:0.02em}
        .row{display:flex;align-items:center;gap:0.75rem;margin-bottom:1rem}
        .pnum{background:var(--gradient-purple);padding:0.65rem 0.85rem;border-radius:var(--radius-md);font-weight:800;font-size:0.9rem;color:white;min-width:44px;text-align:center;box-shadow:0 4px 12px rgba(99,102,241,0.3)}
        .row input{flex:1;padding:1rem 1.1rem;border:2px solid rgba(255,255,255,0.08);border-radius:var(--radius-md);background:rgba(0,0,0,0.4);color:var(--text-primary);font-family:'Outfit',sans-serif;font-size:1.05rem;font-weight:500;transition:all 0.25s ease}
        .row input:focus{outline:none;border-color:var(--accent-primary);background:rgba(99,102,241,0.08);box-shadow:0 0 0 4px rgba(99,102,241,0.12)}
        .row input::placeholder{color:var(--text-muted)}
        .rm{width:42px;height:42px;border-radius:50%;border:none;background:rgba(239,68,68,0.15);color:var(--accent-danger);font-size:1.4rem;font-weight:700;cursor:pointer;transition:all 0.2s ease}
        .rm:hover{background:var(--accent-danger);color:white;transform:scale(1.08)}
        .add{width:100%;padding:1rem;border:2px dashed rgba(255,255,255,0.12);border-radius:var(--radius-md);background:transparent;color:var(--text-secondary);font-family:'Outfit',sans-serif;font-weight:600;font-size:0.95rem;cursor:pointer;margin-bottom:1.5rem;transition:all 0.2s ease}
        .add:hover{border-color:var(--accent-primary);color:var(--accent-primary);background:rgba(99,102,241,0.05)}
        .btn{width:100%;padding:1.15rem;border:none;border-radius:var(--radius-lg);background:var(--gradient-green);color:white;font-family:'Outfit',sans-serif;font-size:1.15rem;font-weight:800;text-transform:uppercase;letter-spacing:0.06em;cursor:pointer;box-shadow:0 6px 24px rgba(16,185,129,0.35);transition:all 0.25s ease}
        .btn:hover:not(:disabled){transform:translateY(-3px);box-shadow:0 10px 36px rgba(16,185,129,0.45)}
        .btn:disabled{opacity:0.4;cursor:not-allowed}
        .btn-row{display:flex;gap:1rem;margin-top:1rem}
        .btn-secondary{background:var(--gradient-purple);box-shadow:0 6px 24px rgba(99,102,241,0.3)}
        .btn-secondary:hover:not(:disabled){box-shadow:0 10px 36px rgba(99,102,241,0.4)}
        
        .char-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1.25rem;margin-bottom:2rem;max-width:540px;width:100%}
        @media(max-width:500px){.char-grid{grid-template-columns:repeat(2,1fr);gap:1rem}}
        .char-card{background:rgba(255,255,255,0.02);border:3px solid var(--c);border-radius:var(--radius-lg);padding:1.5rem 1rem;text-align:center;cursor:pointer;transition:all 0.3s ease;position:relative;overflow:hidden}
        .char-card::before{content:'';position:absolute;inset:0;background:linear-gradient(135deg,var(--c),transparent);opacity:0.08;transition:opacity 0.3s ease}
        .char-card:hover:not(.selected){transform:translateY(-6px) scale(1.02);box-shadow:0 12px 40px color-mix(in srgb,var(--c) 35%,transparent)}
        .char-card:hover:not(.selected)::before{opacity:0.18}
        .char-card.selected{opacity:0.25;filter:grayscale(100%);cursor:not-allowed;border-color:var(--text-muted)}
        .char-avatar{font-size:3.5rem;margin-bottom:0.75rem;display:flex;justify-content:center;align-items:center;height:90px;position:relative;z-index:1}
        .char-avatar .char-svg{width:80px;height:80px;object-fit:contain;filter:drop-shadow(0 6px 16px rgba(0,0,0,0.4))}
        .char-name{font-weight:700;color:var(--c);font-size:1rem;position:relative;z-index:1}
        .rand{padding:1.1rem 2.75rem;border:none;border-radius:var(--radius-lg);background:var(--gradient-orange);color:white;font-family:'Outfit',sans-serif;font-weight:800;font-size:1.15rem;cursor:pointer;box-shadow:0 6px 24px rgba(249,115,22,0.4);transition:all 0.25s ease}
        .rand:hover{transform:translateY(-3px);box-shadow:0 10px 36px rgba(249,115,22,0.5)}
        
        .game-screen{flex:1;display:flex;flex-direction:column;overflow:hidden;position:relative;z-index:1}
        .header{display:flex;justify-content:space-between;align-items:center;padding:0.5rem 0.6rem;background:rgba(0,0,0,0.5);backdrop-filter:blur(12px);border-bottom:1px solid rgba(255,255,255,0.06)}
        @media(min-width:400px){.header{padding:0.6rem 0.8rem}}
        @media(min-width:500px){.header{padding:0.65rem 1rem}}
        .header h1{font-family:'Outfit',sans-serif;font-weight:900;font-size:1.1rem;background:var(--gradient-rainbow);-webkit-background-clip:text;-webkit-text-fill-color:transparent}
        @media(min-width:400px){.header h1{font-size:1.2rem}}
        @media(min-width:500px){.header h1{font-size:1.35rem}}
        .header-btns{display:flex;gap:0.4rem}
        @media(min-width:400px){.header-btns{gap:0.5rem}}
        @media(min-width:500px){.header-btns{gap:0.6rem}}
        .header-btn{padding:0.35rem 0.6rem;border:none;border-radius:var(--radius-sm);color:white;font-family:'Outfit',sans-serif;font-weight:700;font-size:0.7rem;cursor:pointer;transition:all 0.2s ease}
        @media(min-width:400px){.header-btn{padding:0.4rem 0.7rem;font-size:0.75rem}}
        @media(min-width:500px){.header-btn{padding:0.45rem 0.85rem;font-size:0.8rem}}
        .rules-btn{background:rgba(99,102,241,0.7)}
        .new-btn{background:rgba(239,68,68,0.7)}
        .sound-btn{background:rgba(16,185,129,0.7)}
        .header-btn:hover{transform:scale(1.08);filter:brightness(1.1)}
        
        .main-area{flex:1;display:flex;flex-direction:column;padding:0.35rem;overflow:hidden;min-height:0}
        @media(min-width:400px){.main-area{padding:0.5rem}}
        @media(min-width:700px){.main-area{padding:0.75rem}}
        .board-wrap{flex:1;background:rgba(0,0,0,0.35);border-radius:var(--radius-md);padding:0.4rem;display:flex;flex-direction:column;overflow:hidden;min-height:0;border:1px solid rgba(255,255,255,0.05)}
        .board-zoom-container{flex:1;overflow:hidden;position:relative;display:flex;align-items:center;justify-content:center}
        .board{display:grid;grid-template-columns:repeat(20,1fr);gap:2px;width:100%;height:100%;transition:transform 0.6s cubic-bezier(0.25, 0.1, 0.25, 1)}
        .sq{background:rgba(255,255,255,0.035);border-radius:3px;display:flex;flex-direction:column;align-items:center;justify-content:center;position:relative;min-height:0;transition:background 0.15s ease}
        .sq:hover{background:rgba(255,255,255,0.07)}
        .sq-num{color:#ffffff;position:absolute;top:1px;left:2px;font-size:0.5rem;font-weight:600}
        .special{font-size:0.55rem;font-weight:800}
        .sq.stair{background:linear-gradient(145deg,#059669 0%,#10B981 100%);box-shadow:inset 0 1px 0 rgba(255,255,255,0.15)}
        .sq.stair .sq-num{color:rgba(255,255,255,0.75)}
        .sq.stair .special{color:#A7F3D0}
        .sq.slide{background:linear-gradient(145deg,#DC2626 0%,#EF4444 100%);box-shadow:inset 0 1px 0 rgba(255,255,255,0.1)}
        .sq.slide .sq-num{color:rgba(255,255,255,0.75)}
        .sq.slide .special{color:#FECACA}
        .sq.finish{background:linear-gradient(145deg,#D97706,#F59E0B)}
        .sq.finish::after{content:'🏆';position:absolute;font-size:1.4rem}
        @media(min-width:400px){.sq.finish::after{font-size:1.6rem}}
        @media(min-width:500px){.sq.finish::after{font-size:1.8rem}}
        @media(min-width:700px){.sq.finish::after{font-size:2.2rem}}
        .sq.start{background:linear-gradient(145deg,#4F46E5,#6366F1)}
        .tokens{position:absolute;display:flex;justify-content:center;align-items:center;z-index:5;width:100%;height:100%}
        .token{width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 4px 12px rgba(0,0,0,0.5),0 0 0 1px rgba(0,0,0,0.2);position:absolute;overflow:hidden;transition:transform 0.15s ease}
        @media(min-width:400px){.token{width:42px;height:42px;border:3px solid white}}
        @media(min-width:500px){.token{width:50px;height:50px;border:3px solid white}}
        @media(min-width:700px){.token{width:58px;height:58px;border:4px solid white;box-shadow:0 6px 20px rgba(0,0,0,0.5),0 0 0 2px rgba(0,0,0,0.2)}}
        @media(min-width:900px){.token{width:65px;height:65px}}
        .token.stair-pulse{animation:stairPulse 0.3s ease-in-out 3}
        .token.slide-pulse{animation:slidePulse 0.3s ease-in-out 3}
        @keyframes stairPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.4);box-shadow:0 0 35px rgba(16,185,129,0.85),0 6px 20px rgba(0,0,0,0.5)}}
        @keyframes slidePulse{0%,100%{transform:scale(1)}50%{transform:scale(0.65);box-shadow:0 0 35px rgba(239,68,68,0.85),0 6px 20px rgba(0,0,0,0.5)}}
        .token .avatar-tiny{font-size:1.3rem}
        @media(min-width:400px){.token .avatar-tiny{font-size:1.5rem}}
        @media(min-width:500px){.token .avatar-tiny{font-size:1.7rem}}
        @media(min-width:700px){.token .avatar-tiny{font-size:2rem}}
        .token .char-svg.avatar-tiny{width:28px;height:28px;object-fit:contain}
        @media(min-width:400px){.token .char-svg.avatar-tiny{width:34px;height:34px}}
        @media(min-width:500px){.token .char-svg.avatar-tiny{width:40px;height:40px}}
        @media(min-width:700px){.token .char-svg.avatar-tiny{width:48px;height:48px}}
        @media(min-width:900px){.token .char-svg.avatar-tiny{width:52px;height:52px}}
        
        .start-block{display:inline-flex;align-items:center;gap:0.5rem;padding:0.4rem 0.7rem;background:linear-gradient(135deg,rgba(79,70,229,0.2),rgba(99,102,241,0.2));border-radius:var(--radius-md);border:2px solid rgba(99,102,241,0.4)}
        @media(min-width:400px){.start-block{gap:0.6rem;padding:0.5rem 0.8rem}}
        @media(min-width:500px){.start-block{gap:0.75rem;padding:0.6rem 1rem}}
        .start-label{color:#A5B4FC;font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:0.05em}
        @media(min-width:400px){.start-label{font-size:0.75rem}}
        @media(min-width:500px){.start-label{font-size:0.8rem}}
        .start-tokens{display:flex;gap:0.3rem}
        @media(min-width:400px){.start-tokens{gap:0.4rem}}
        @media(min-width:500px){.start-tokens{gap:0.5rem}}
        .token.start-token{width:32px;height:32px;font-size:1.2rem;border:2px solid white}
        @media(min-width:400px){.token.start-token{width:38px;height:38px;font-size:1.3rem}}
        @media(min-width:500px){.token.start-token{width:44px;height:44px;font-size:1.4rem}}
        @media(min-width:700px){.token.start-token{width:48px;height:48px;font-size:1.5rem;border:3px solid white}}
        .token.start-token .avatar-sm{font-size:1rem}
        @media(min-width:400px){.token.start-token .avatar-sm{font-size:1.1rem}}
        @media(min-width:500px){.token.start-token .avatar-sm{font-size:1.2rem}}
        @media(min-width:700px){.token.start-token .avatar-sm{font-size:1.3rem}}
        .token.start-token .char-svg.avatar-sm{width:24px;height:24px}
        @media(min-width:400px){.token.start-token .char-svg.avatar-sm{width:30px;height:30px}}
        @media(min-width:500px){.token.start-token .char-svg.avatar-sm{width:34px;height:34px}}
        @media(min-width:700px){.token.start-token .char-svg.avatar-sm{width:38px;height:38px}}
        .bottom-section{display:flex;flex-direction:column;gap:0.5rem;align-items:center;padding:0.4rem;margin-top:0.25rem;min-height:60px;width:100%;box-sizing:border-box}
        @media(min-width:400px){.bottom-section{gap:0.6rem;padding:0.5rem;min-height:65px}}
        @media(min-width:500px){.bottom-section{flex-direction:row;gap:1rem;min-height:70px}}
        .start-col{flex-shrink:0}
        .next-col{flex:1;display:flex;justify-content:center;min-width:0;max-width:100%}
        
        .next-section{display:flex;flex-direction:column;align-items:center;text-align:center;max-width:100%;padding:0 0.5rem}
        .turn-done{font-size:0.8rem;color:#A5B4FC;margin-bottom:0.5rem;display:flex;align-items:center;gap:0.4rem;flex-wrap:wrap;justify-content:center}
        @media(min-width:500px){.turn-done{font-size:0.9rem;gap:0.5rem;margin-bottom:0.6rem}}
        .turn-done-token{width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 4px 12px rgba(0,0,0,0.4);flex-shrink:0}
        @media(min-width:500px){.turn-done-token{width:44px;height:44px;border:3px solid white}}
        .turn-done-token .char-svg.avatar-sm{width:28px;height:28px}
        @media(min-width:500px){.turn-done-token .char-svg.avatar-sm{width:34px;height:34px}}
        .moving-txt{font-size:0.85rem;font-weight:600;color:var(--accent-warning)}
        @media(min-width:500px){.moving-txt{font-size:1rem}}
        .next-btn{padding:0.7rem 1.5rem;border:none;border-radius:var(--radius-md);background:var(--gradient-green);color:white;font-family:'Outfit',sans-serif;font-size:0.95rem;font-weight:800;cursor:pointer;text-transform:uppercase;letter-spacing:0.03em;white-space:nowrap;box-shadow:0 4px 16px rgba(16,185,129,0.4);transition:all 0.2s ease}
        @media(min-width:500px){.next-btn{padding:0.9rem 2.2rem;font-size:1.1rem}}
        .next-btn:hover{transform:translateY(-2px);box-shadow:0 6px 24px rgba(16,185,129,0.5)}
        .next-btn.pulse{animation:btnPulse 2s ease-in-out infinite}
        @keyframes btnPulse{0%,100%{transform:scale(1);box-shadow:0 4px 16px rgba(16,185,129,0.4)}50%{transform:scale(1.03);box-shadow:0 6px 28px rgba(16,185,129,0.6)}}
        
        .overlay{position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.92);backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center;z-index:100;padding:1rem;padding-top:max(1rem,env(safe-area-inset-top));padding-bottom:max(1rem,env(safe-area-inset-bottom));overflow-y:auto}
        .popup{background:linear-gradient(145deg,var(--bg-elevated),var(--bg-secondary));border-radius:var(--radius-xl);padding:1rem;text-align:center;width:95%;max-width:520px;max-height:calc(100vh - 2rem);max-height:calc(100dvh - 2rem);border:1px solid rgba(255,255,255,0.08);box-shadow:0 25px 80px rgba(0,0,0,0.5);margin:auto;display:flex;flex-direction:column;overflow-y:auto}
        @media(min-width:400px){.popup{padding:1rem;max-height:calc(100vh - 2rem);max-height:calc(100dvh - 2rem)}}
        @media(min-width:500px){.popup{padding:1.25rem;max-height:calc(100vh - 3rem);max-height:calc(100dvh - 3rem)}}
        @media(min-width:700px){.popup{max-width:900px;padding:1.5rem;max-height:95vh;max-height:95dvh}}
        @media(min-width:1000px){.popup{max-width:1100px;padding:1.75rem}}
        
        .intro-header{display:flex;align-items:center;justify-content:center;gap:1rem;margin-bottom:1rem;flex-wrap:wrap}
        .intro-avatar{width:60px;height:60px;display:flex;align-items:center;justify-content:center}
        @media(min-width:400px){.intro-avatar{width:70px;height:70px}}
        @media(min-width:500px){.intro-avatar{width:85px;height:85px}}
        @media(min-width:700px){.intro-avatar{width:100px;height:100px}}
        @media(min-width:900px){.intro-avatar{width:120px;height:120px}}
        .intro-avatar .char-svg{width:100%;height:100%;filter:drop-shadow(0 8px 24px rgba(0,0,0,0.5))}
        .intro-avatar .avatar-md{font-size:4.5rem}
        .intro-name{font-family:'Outfit',sans-serif;font-weight:900;font-size:1.4rem;color:var(--pc);text-shadow:0 2px 24px color-mix(in srgb,var(--pc) 40%,transparent)}
        @media(min-width:400px){.intro-name{font-size:1.6rem}}
        @media(min-width:500px){.intro-name{font-size:2rem}}
        @media(min-width:700px){.intro-name{font-size:2.4rem}}
        @media(min-width:900px){.intro-name{font-size:3rem}}
        
        .wheel-section{display:flex;flex-direction:column;align-items:center;gap:0.5rem;margin:0.25rem 0;flex:1;justify-content:center}
        @media(min-width:700px){.wheel-section{flex-direction:row;justify-content:center;gap:3rem;margin:1rem 0}}
        .wheel-wrap{position:relative;width:min(48vw,200px);height:min(48vw,200px);flex-shrink:0}
        @media(min-width:400px){.wheel-wrap{width:min(52vw,230px);height:min(52vw,230px)}}
        @media(min-width:500px){.wheel-wrap{width:min(50vw,280px);height:min(50vw,280px)}}
        @media(min-width:700px){.wheel-wrap{width:min(45vw,350px);height:min(45vw,350px)}}
        @media(min-width:900px){.wheel-wrap{width:min(40vw,420px);height:min(40vw,420px)}}
        @media(min-width:1100px){.wheel-wrap{width:min(38vw,500px);height:min(38vw,500px)}}
        .wheel-ptr{position:absolute;top:-15px;left:50%;transform:translateX(-50%);font-size:clamp(1.5rem,3.5vw,3rem);z-index:10;filter:drop-shadow(0 4px 8px rgba(0,0,0,0.6))}
        .wheel-svg{width:calc(100% - 12px);height:calc(100% - 12px);position:absolute;top:6px;left:6px;transition:transform 4s cubic-bezier(0.2,0.8,0.3,1);filter:drop-shadow(0 10px 40px rgba(0,0,0,0.5))}
        .wheel-legend{background:rgba(0,0,0,0.4);border-radius:var(--radius-md);padding:0.4rem;border:1px solid rgba(255,255,255,0.06);display:flex;flex-wrap:wrap;gap:0.25rem;justify-content:center;max-width:95vw;font-size:0.7rem}
        @media(min-width:400px){.wheel-legend{padding:0.5rem;gap:0.3rem;font-size:0.75rem}}
        @media(min-width:500px){.wheel-legend{padding:0.6rem;gap:0.35rem;font-size:0.8rem}}
        @media(min-width:700px){.wheel-legend{flex-direction:column;padding:1rem;gap:0.5rem;font-size:0.85rem}}
        .legend-title{font-weight:800;color:var(--accent-warning);margin-bottom:0.25rem;text-align:center;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.1em;width:100%}
        @media(min-width:500px){.legend-title{font-size:0.8rem}}
        @media(min-width:800px){.legend-title{font-size:1rem}}
        .legend-item{display:flex;align-items:center;gap:0.3rem}
        @media(min-width:500px){.legend-item{gap:0.5rem}}
        .legend-icon{width:22px;height:22px;border-radius:var(--radius-sm);display:flex;align-items:center;justify-content:center;font-size:0.75rem;flex-shrink:0}
        @media(min-width:500px){.legend-icon{width:26px;height:26px;font-size:0.85rem}}
        @media(min-width:800px){.legend-icon{width:36px;height:36px;font-size:1.1rem}}
        .legend-text{color:var(--text-secondary);font-size:0.65rem;font-weight:500}
        @media(min-width:500px){.legend-text{font-size:0.75rem}}
        @media(min-width:800px){.legend-text{font-size:0.95rem}}
        
        .spin-btn{padding:0.6rem 1.5rem;border:none;border-radius:50px;background:var(--gradient-orange);color:white;font-family:'Outfit',sans-serif;font-size:1rem;font-weight:900;cursor:pointer;text-transform:uppercase;letter-spacing:0.1em;margin-top:0.25rem;box-shadow:0 8px 36px rgba(249,115,22,0.5);transition:all 0.25s ease}
        @media(min-width:400px){.spin-btn{padding:0.7rem 1.8rem;font-size:1.1rem;margin-top:0.4rem}}
        @media(min-width:500px){.spin-btn{padding:0.9rem 2.5rem;font-size:1.3rem;margin-top:0.5rem}}
        @media(min-width:700px){.spin-btn{padding:1.1rem 3.5rem;font-size:1.6rem}}
        @media(min-width:900px){.spin-btn{padding:1.3rem 4rem;font-size:1.8rem}}
        .spin-btn:hover:not(:disabled){transform:translateY(-3px) scale(1.02);box-shadow:0 12px 48px rgba(249,115,22,0.6)}
        .spin-btn:disabled{opacity:0.5;cursor:not-allowed}
        .spinning-text{color:var(--accent-warning);font-size:1.4rem;font-weight:700;margin-top:1rem}
        
        .ability-card{padding:1.5rem;border-radius:var(--radius-xl);margin-bottom:1rem;box-shadow:0 8px 32px rgba(0,0,0,0.3)}
        @media(min-width:500px){.ability-card{padding:2rem;margin-bottom:1.5rem}}
        @media(min-width:800px){.ability-card{padding:2.5rem}}
        .ability-icon{font-size:3.5rem;margin-bottom:0.5rem;filter:drop-shadow(0 4px 12px rgba(0,0,0,0.3))}
        @media(min-width:500px){.ability-icon{font-size:4.5rem;margin-bottom:0.75rem}}
        @media(min-width:800px){.ability-icon{font-size:5.5rem}}
        .ability-name{font-size:1.6rem;font-weight:900;margin-bottom:0.4rem}
        @media(min-width:500px){.ability-name{font-size:2rem;margin-bottom:0.5rem}}
        @media(min-width:800px){.ability-name{font-size:2.4rem}}
        .ability-desc{opacity:0.95;font-size:1rem;font-weight:500}
        @media(min-width:500px){.ability-desc{font-size:1.15rem}}
        @media(min-width:800px){.ability-desc{font-size:1.25rem}}
        
        .ready{font-family:'Outfit',sans-serif;font-weight:900;font-size:3.5rem;color:var(--accent-success);text-shadow:0 0 60px rgba(16,185,129,0.6);animation:readyPulse 0.5s ease-in-out infinite alternate}
        @media(min-width:500px){.ready{font-size:4.5rem}}
        @media(min-width:800px){.ready{font-size:5.5rem}}
        @keyframes readyPulse{from{transform:scale(1);opacity:1}to{transform:scale(1.05);opacity:0.9}}
        
        .play-popup{width:100%;max-width:1000px;display:flex;flex-direction:column}
        .play-header{display:flex;align-items:center;justify-content:center;gap:0.5rem;margin-bottom:0.25rem}
        .play-avatar{width:40px;height:40px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 3px 10px rgba(0,0,0,0.3)}
        .play-avatar .char-svg.avatar-sm{width:32px;height:32px}
        .play-name{font-family:'Outfit',sans-serif;font-weight:800;font-size:1.2rem;color:var(--accent-warning)}
        
        .timer{font-size:3rem;font-weight:900;font-family:'Space Grotesk',monospace;color:var(--accent-success);margin-bottom:0.25rem;text-shadow:0 0 30px rgba(16,185,129,0.4);line-height:1}
        @media(min-width:450px){.timer{font-size:3.5rem}}
        @media(min-width:700px){.timer{font-size:4rem}}
        @media(min-width:1000px){.timer{font-size:4.5rem}}
        .timer.urgent{color:var(--accent-danger);animation:urgentPulse 0.5s ease-in-out infinite;text-shadow:0 0 30px rgba(239,68,68,0.5)}
        @keyframes urgentPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.1)}}
        
        .hint-txt{font-size:0.65rem;color:var(--text-muted);margin-bottom:0.2rem;text-transform:uppercase;letter-spacing:0.15em;font-weight:600}
        .letters{display:flex;gap:0.4rem;justify-content:center;margin-bottom:0.2rem}
        .letter{width:50px;height:50px;background:var(--gradient-orange);border-radius:var(--radius-md);display:flex;align-items:center;justify-content:center;font-size:1.6rem;font-weight:900;box-shadow:0 4px 16px rgba(249,115,22,0.4);border:3px solid rgba(255,255,255,0.2);transition:all 0.2s ease}
        @media(min-width:450px){.letter{width:56px;height:56px;font-size:1.8rem}}
        @media(min-width:650px){.letter{width:64px;height:64px;font-size:2rem}}
        @media(min-width:900px){.letter{width:72px;height:72px;font-size:2.3rem;border-radius:var(--radius-lg)}}
        .letter.used{background:var(--gradient-green);transform:scale(1.05);box-shadow:0 6px 24px rgba(16,185,129,0.5)}
        
        .live-score-section{margin:0.25rem 0;min-height:52px;display:flex;flex-direction:column;justify-content:center}
        .potential-score{display:flex;align-items:center;justify-content:center;gap:0.5rem;margin-bottom:0.15rem}
        .score-label{font-size:0.7rem;color:var(--text-secondary);font-weight:500}
        .score-value{font-size:1rem;font-weight:800;color:var(--accent-success)}
        .score-value.low{color:var(--accent-danger)}
        .score-value.high{color:var(--accent-warning);text-shadow:0 0 16px rgba(251,191,36,0.5)}
        .multiplier-display{text-align:center;font-size:0.65rem;color:#A5B4FC;margin-bottom:0.15rem;font-weight:500}
        .mult-bad{color:#F87171}
        .mult-good{color:#6EE7B7}
        .key-tip{font-size:0.65rem;text-align:center;padding:0.35rem 0.6rem;border-radius:var(--radius-sm);background:rgba(251,191,36,0.12);color:var(--accent-warning);font-weight:600}
        .key-tip.flash{animation:tipFlash 1.2s ease-in-out infinite}
        .key-tip.perfect{background:rgba(16,185,129,0.15);color:#6EE7B7}
        @keyframes tipFlash{0%,100%{opacity:1}50%{opacity:0.6}}
        
        .ab-btns{display:flex;gap:0.75rem;justify-content:center;margin:0.4rem 0}
        .ab-btn{padding:0.6rem 1.2rem;border:none;border-radius:var(--radius-md);font-family:'Outfit',sans-serif;font-weight:700;font-size:0.9rem;cursor:pointer;color:white;transition:all 0.2s ease}
        .ab-btn.scram{background:var(--gradient-purple);box-shadow:0 4px 16px rgba(139,92,246,0.4)}
        .ab-btn.hint{background:linear-gradient(135deg,#F59E0B,#D97706);box-shadow:0 4px 16px rgba(245,158,11,0.4)}
        .ab-btn:hover{transform:translateY(-2px)}
        .hint-disp{background:rgba(251,191,36,0.12);border:2px solid var(--accent-warning);padding:0.5rem 1rem;border-radius:var(--radius-md);color:var(--accent-warning);margin-bottom:0.4rem;font-size:1rem;font-weight:700}
        .note{font-size:0.9rem;padding:0.55rem 1rem;border-radius:var(--radius-sm);margin-bottom:0.5rem;display:inline-block;font-weight:600}
        .note.warning{color:var(--accent-warning);background:rgba(251,191,36,0.1)}
        .note.success{color:var(--accent-success);background:rgba(16,185,129,0.12)}
        
        .word-box{background:rgba(0,0,0,0.5);border:2px solid rgba(255,255,255,0.1);border-radius:var(--radius-md);padding:0.7rem;min-height:46px;font-size:1.5rem;font-weight:800;text-align:center;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:0.4rem;color:var(--text-primary);width:100%;max-width:900px;margin-left:auto;margin-right:auto}
        @media(min-width:450px){.word-box{padding:0.8rem;min-height:50px;font-size:1.7rem}}
        @media(min-width:700px){.word-box{padding:0.9rem;min-height:54px;font-size:1.9rem}}
        @media(min-width:1000px){.word-box{padding:1rem;min-height:58px;font-size:2.1rem}}
        .placeholder{color:var(--text-muted);font-size:1rem;font-weight:500;text-transform:none;letter-spacing:normal}
        
        .keyboard{display:flex;flex-direction:column;gap:4px;width:100%;margin-bottom:0.5rem;max-width:900px;margin-left:auto;margin-right:auto}
        @media(min-width:600px){.keyboard{gap:5px}}
        @media(min-width:900px){.keyboard{gap:6px}}
        .kb-row{display:flex;justify-content:center;gap:4px}
        @media(min-width:600px){.kb-row{gap:5px}}
        @media(min-width:900px){.kb-row{gap:6px}}
        .key{flex:1;max-width:52px;height:42px;border:none;border-radius:var(--radius-sm);background:rgba(255,255,255,0.08);color:var(--text-primary);font-family:'Outfit',sans-serif;font-size:1rem;font-weight:700;cursor:pointer;padding:0 3px;transition:all 0.1s ease;border:2px solid transparent}
        @media(min-width:400px){.key{height:46px;font-size:1.1rem;max-width:56px}}
        @media(min-width:550px){.key{height:52px;font-size:1.25rem;max-width:65px}}
        @media(min-width:750px){.key{height:58px;font-size:1.4rem;border-radius:var(--radius-md);max-width:78px}}
        @media(min-width:1000px){.key{height:64px;font-size:1.5rem;max-width:90px}}
        .key:hover:not(:disabled){background:rgba(255,255,255,0.15)}
        .key:active:not(:disabled){transform:scale(0.95)}
        .key:disabled{opacity:0.3;cursor:not-allowed}
        .key.bonus{background:var(--gradient-purple);border-color:rgba(255,255,255,0.2);box-shadow:0 2px 8px rgba(99,102,241,0.4)}
        .key.used-bonus{background:var(--gradient-green);border-color:rgba(255,255,255,0.3);box-shadow:0 2px 8px rgba(16,185,129,0.5)}
        .key.backspace{flex:1.5;max-width:78px;background:rgba(239,68,68,0.5)}
        @media(min-width:400px){.key.backspace{max-width:84px}}
        @media(min-width:550px){.key.backspace{max-width:97px}}
        @media(min-width:750px){.key.backspace{max-width:117px}}
        @media(min-width:1000px){.key.backspace{max-width:135px}}
        .key.backspace:hover:not(:disabled){background:rgba(239,68,68,0.7)}
        
        .submit-btn{width:100%;max-width:900px;margin-left:auto;margin-right:auto;padding:0.85rem;border:none;border-radius:var(--radius-lg);background:var(--gradient-green);color:white;font-family:'Outfit',sans-serif;font-size:1.05rem;font-weight:800;cursor:pointer;text-transform:uppercase;letter-spacing:0.05em;box-shadow:0 4px 20px rgba(16,185,129,0.4);transition:all 0.2s ease;display:block}
        @media(min-width:450px){.submit-btn{padding:0.95rem;font-size:1.1rem}}
        @media(min-width:700px){.submit-btn{padding:1.05rem;font-size:1.2rem}}
        @media(min-width:1000px){.submit-btn{padding:1.15rem;font-size:1.3rem}}
        .submit-btn:hover:not(:disabled){transform:translateY(-2px);box-shadow:0 6px 28px rgba(16,185,129,0.5)}
        .submit-btn:disabled{opacity:0.4;cursor:not-allowed}
        
        .result-card{padding:1rem 1.25rem;border-radius:var(--radius-xl);margin-bottom:0.75rem;box-shadow:0 8px 32px rgba(0,0,0,0.3)}
        @media(min-width:400px){.result-card{padding:1.25rem 1.5rem;margin-bottom:1rem}}
        @media(min-width:500px){.result-card{padding:1.5rem 2rem;margin-bottom:1.25rem}}
        @media(min-width:700px){.result-card{padding:2rem 2.5rem;margin-bottom:1.5rem}}
        .result-card.valid{background:linear-gradient(135deg,#059669,#10B981)}
        .result-card.invalid{background:linear-gradient(135deg,#DC2626,#EF4444)}
        .result-icon{font-size:2.5rem;margin-bottom:0.3rem}
        @media(min-width:400px){.result-icon{font-size:3rem;margin-bottom:0.4rem}}
        @media(min-width:500px){.result-icon{font-size:4rem;margin-bottom:0.5rem}}
        @media(min-width:700px){.result-icon{font-size:5rem;margin-bottom:0.75rem}}
        .result-word{font-size:1.3rem;font-weight:900}
        @media(min-width:400px){.result-word{font-size:1.5rem}}
        @media(min-width:500px){.result-word{font-size:1.8rem}}
        @media(min-width:700px){.result-word{font-size:2.2rem}}
        .result-details{margin:0.3rem 0;opacity:0.95;font-size:0.85rem;line-height:1.4;font-weight:500}
        @media(min-width:400px){.result-details{margin:0.4rem 0;font-size:0.9rem}}
        @media(min-width:500px){.result-details{margin:0.5rem 0;font-size:1rem;line-height:1.5}}
        @media(min-width:700px){.result-details{margin:0.75rem 0;font-size:1.15rem;line-height:1.6}}
        .result-points{font-size:1.8rem;font-weight:900;font-family:'Space Grotesk',sans-serif}
        @media(min-width:400px){.result-points{font-size:2.2rem}}
        @media(min-width:500px){.result-points{font-size:2.6rem}}
        @media(min-width:700px){.result-points{font-size:3.2rem}}
        
        .winner-card{background:linear-gradient(135deg,var(--wc),rgba(255,255,255,0.15));padding:2.25rem;border-radius:var(--radius-xl);text-align:center;margin-bottom:1.5rem;box-shadow:0 12px 48px rgba(0,0,0,0.4)}
        .winner-avatar{font-size:4rem;display:flex;justify-content:center;margin-bottom:0.75rem}
        .winner-avatar .char-svg{width:110px;height:110px;filter:drop-shadow(0 8px 28px rgba(0,0,0,0.4))}
        .winner-name{font-family:'Outfit',sans-serif;font-weight:900;font-size:2.75rem;margin-bottom:0.5rem}
        .winner-score{opacity:0.9;font-size:1.15rem;font-weight:600}
        .standings{background:rgba(255,255,255,0.05);padding:1.1rem;border-radius:var(--radius-md);width:100%;max-width:360px;margin-bottom:1.5rem;border:1px solid rgba(255,255,255,0.06)}
        .standings h3{margin-bottom:0.75rem;text-align:center;font-size:1.05rem;font-weight:700;color:var(--text-secondary)}
        .stand-row{display:flex;align-items:center;gap:0.5rem;padding:0.55rem;background:rgba(255,255,255,0.05);border-radius:var(--radius-sm);margin-bottom:0.4rem;font-size:0.95rem}
        
        .rules-overlay{position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.95);backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center;z-index:200;padding:1rem}
        .rules-content{background:linear-gradient(145deg,var(--bg-elevated),var(--bg-secondary));border-radius:var(--radius-xl);padding:1.5rem;max-width:520px;max-height:90vh;max-height:90dvh;overflow-y:auto;border:1px solid rgba(255,255,255,0.08)}
        .rules-content h2{text-align:center;margin-bottom:1.25rem;font-size:1.6rem;font-weight:800}
        .rule-section{margin-bottom:1.25rem;background:rgba(0,0,0,0.25);padding:1rem;border-radius:var(--radius-md)}
        .rule-section.highlight-section{background:linear-gradient(135deg,rgba(139,92,246,0.15),rgba(59,130,246,0.15));border:2px solid rgba(139,92,246,0.3)}
        .rule-section h3{color:var(--accent-warning);margin-bottom:0.5rem;font-size:1.05rem;font-weight:800}
        .rule-section p,.rule-section li{font-size:0.9rem;color:var(--text-secondary);line-height:1.5}
        .rule-section ol,.rule-section ul{padding-left:1.5rem;margin:0.4rem 0}
        .rule-section li{margin-bottom:0.3rem}
        .stair-label{background:var(--accent-success);padding:0.15rem 0.4rem;border-radius:4px;font-size:0.8rem;font-weight:700;color:#ffffff}
        .slide-label{background:var(--accent-danger);padding:0.15rem 0.4rem;border-radius:4px;font-size:0.8rem;font-weight:700;color:#ffffff}
        .warning-text{color:var(--accent-warning);background:rgba(251,191,36,0.12);padding:0.45rem 0.65rem;border-radius:var(--radius-sm);margin-top:0.5rem;font-weight:600;font-size:0.85rem}
        .key-tip{color:var(--accent-secondary);background:rgba(16,185,129,0.15);padding:0.6rem 0.8rem;border-radius:var(--radius-sm);margin-top:0.75rem;font-weight:600;font-size:0.95rem;text-align:center}
        .close-rules-btn{width:100%;padding:0.95rem;border:none;border-radius:var(--radius-md);background:var(--gradient-green);color:white;font-family:'Outfit',sans-serif;font-size:1.05rem;font-weight:800;cursor:pointer;margin-top:0.75rem;box-shadow:0 4px 16px rgba(16,185,129,0.4)}
        
        .scoring-box{background:rgba(0,0,0,0.3);border-radius:var(--radius-sm);padding:0.8rem;margin:0.6rem 0}
        .scoring-formula{display:flex;align-items:center;justify-content:center;gap:0.5rem;flex-wrap:wrap}
        .formula-part{background:rgba(255,255,255,0.1);padding:0.4rem 0.65rem;border-radius:var(--radius-sm);font-size:0.85rem;font-weight:700}
        .formula-part.result{background:var(--gradient-green);color:white}
        .formula-x,.formula-eq{font-size:1.1rem;font-weight:800;color:var(--accent-warning)}
        .example-box{background:rgba(0,0,0,0.25);border-radius:var(--radius-sm);padding:0.65rem;margin-top:0.6rem}
        .example-title{font-size:0.85rem;color:#A5B4FC;margin-bottom:0.5rem;text-align:center}
        .example-row{display:flex;align-items:center;gap:0.5rem;padding:0.45rem 0.55rem;border-radius:var(--radius-sm);margin-bottom:0.3rem;font-size:0.8rem}
        .example-row.bad{background:rgba(239,68,68,0.12)}
        .example-row.ok{background:rgba(251,191,36,0.12)}
        .example-row.great{background:rgba(16,185,129,0.12)}
        .ex-word{font-weight:800;min-width:80px;font-family:'Space Grotesk',monospace}
        .ex-calc{flex:1;color:var(--text-secondary)}
        .ex-note{font-size:0.75rem;color:var(--text-muted)}
        
        .avatar-lg{font-size:3rem}
        .avatar-md{font-size:1.75rem}
        .avatar-sm{font-size:1.1rem}
        .avatar-tiny{font-size:0.8rem}
        .char-svg.avatar-lg{width:72px;height:72px;object-fit:contain}
        .char-svg.avatar-md{width:42px;height:42px;object-fit:contain}
        .char-svg.avatar-sm{width:28px;height:28px;object-fit:contain}
        .char-svg.avatar-tiny{width:18px;height:18px;object-fit:contain}
      `}</style>

      {showRules && <Rules onClose={() => setShowRules(false)} />}

      {gamePhase === 'setup' && (
        <div className="setup">
          <h1 className="title">WORD SLIDE</h1>
          <p className="subtitle">A Family Word Game</p>
          <div className="form">
            <h2>Enter Player Names</h2>
            {playerNames.map((name, i) => (
              <div key={i} className="row">
                <span className="pnum">P{i + 1}</span>
                <input value={name} onChange={(e) => updatePlayerName(i, e.target.value)} placeholder={`Player ${i + 1}`} maxLength={12} />
                {playerNames.length > 2 && <button className="rm" onClick={() => removePlayerSlot(i)}>×</button>}
              </div>
            ))}
            {playerNames.length < 6 && <button className="add" onClick={addPlayerSlot}>+ Add Player</button>}
            <button className="btn" onClick={startCharacterSelect} disabled={playerNames.filter(n => n.trim()).length < 2}>Start Game →</button>
            <div className="btn-row">
              <button className="btn btn-secondary" style={{flex:1}} onClick={() => setShowRules(true)}>📖 Rules</button>
            </div>
          </div>
        </div>
      )}

      {gamePhase === 'character-select' && (
        <div className="char-select">
          <h2 style={{marginBottom:'2rem',fontSize:'2rem'}}>{players[selectingPlayerIndex].name}, Choose Your Character!</h2>
          <div className="char-grid">
            {CHARACTERS.map(char => {
              const isSelected = !availableCharacters.find(c => c.id === char.id);
              return (
                <div 
                  key={char.id} 
                  className={`char-card ${isSelected ? 'selected' : ''}`} 
                  style={{ '--c': char.color }} 
                  onClick={() => !isSelected && selectCharacter(char)}
                >
                  <div className="char-avatar"><CharacterAvatar character={char} size="large" /></div>
                  <div className="char-name">{char.name}</div>
                </div>
              );
            })}
          </div>
          <button className="rand" onClick={() => selectCharacter(availableCharacters[Math.floor(Math.random() * availableCharacters.length)])}>🎲 Random</button>
        </div>
      )}

      {gamePhase === 'playing' && (
        <div className="game-screen">
          <div className="header">
            <h1>WORD SLIDE</h1>
            <div className="header-btns">
              <button className="header-btn sound-btn" onClick={() => { setSoundEnabled(SoundManager.toggle()); }}>{soundEnabled ? '🔊' : '🔇'}</button>
              <button className="header-btn rules-btn" onClick={() => setShowRules(true)}>📖 Rules</button>
              <button className="header-btn new-btn" onClick={startNewGame}>🔄 New Game</button>
            </div>
          </div>
          
          <div className="main-area">
            <div className="board-wrap" ref={boardRef}>
              <div className={`board-zoom-container ${boardView}`}>
                <div className="board" style={getBoardTransform()}>{renderBoard()}</div>
              </div>
            </div>
            
            <div className="bottom-section">
              {/* Column 1: Start block */}
              <div className="start-col">
                {players.some(p => p.position === 0) && (
                  <div className="start-block">
                    <span className="start-label">🏁 Start Area</span>
                    <div className="start-tokens">
                      {players.filter(p => p.position === 0).map(p => (
                        <span key={p.id} className="token start-token" style={{ background: p.character?.color }}>
                          <CharacterAvatar character={p.character} size="small" />
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Column 2: Next player section - always reserve space */}
              <div className="next-col">
                {(turnPhase === 'board-view' || turnPhase === 'animating') && (
                  <div className="next-section">
                    {turnPhase === 'animating' ? (
                      <div className="moving-txt">Moving {currentPlayer?.name}...</div>
                    ) : (
                      <>
                        <div className="turn-done">
                          <span className="turn-done-token" style={{ background: currentPlayer?.character?.color }}>
                            <CharacterAvatar character={currentPlayer?.character} size="small" />
                          </span>
                          <span>{currentPlayer?.name} moved {wordResult?.points || 0} {(wordResult?.points || 0) === 1 ? 'space' : 'spaces'} to square {currentPlayer?.position}!</span>
                        </div>
                        <button className="next-btn pulse" onClick={proceedToNextTurn}>
                          {doubleTurn ? `${currentPlayer?.name}'s Bonus Turn!` : 'Next Player →'}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          


          {['intro', 'spinning', 'ability-reveal', 'ready', 'playing', 'result'].includes(turnPhase) && (
            <div className="overlay">
              <div className="popup">
                {(turnPhase === 'intro' || turnPhase === 'spinning') && currentPlayer && (
                  <>
                    <div className="intro-header" style={{ '--pc': currentPlayer.character?.color }}>
                      <div className="intro-avatar"><CharacterAvatar character={currentPlayer.character} size="medium" /></div>
                      <div className="intro-name">{currentPlayer.name}'s Turn!</div>
                    </div>
                    <div className="wheel-section">
                      <div className="wheel-wrap">
                        <div className="wheel-ptr">▼</div>
                        <svg 
                          ref={wheelRef}
                          className="wheel-svg" 
                          viewBox="0 0 200 200" 
                          style={{ transform: `rotate(${wheelRotation}deg)` }}
                        >
                          {WHEEL_SEGMENTS.map((seg, i) => {
                            const segmentDegrees = 360 / 14;
                            const startAngle = (i * segmentDegrees - 90) * Math.PI / 180;
                            const endAngle = ((i + 1) * segmentDegrees - 90) * Math.PI / 180;
                            const x1 = 100 + 95 * Math.cos(startAngle);
                            const y1 = 100 + 95 * Math.sin(startAngle);
                            const x2 = 100 + 95 * Math.cos(endAngle);
                            const y2 = 100 + 95 * Math.sin(endAngle);
                            const midAngle = ((i * segmentDegrees + segmentDegrees / 2) - 90) * Math.PI / 180;
                            const textX = 100 + 62 * Math.cos(midAngle);
                            const textY = 100 + 62 * Math.sin(midAngle);
                            return (
                              <g key={i}>
                                <path d={`M100,100 L${x1},${y1} A95,95 0 0,1 ${x2},${y2} Z`} fill={seg.color} stroke="rgba(255,255,255,0.3)" strokeWidth="2"/>
                                <text x={textX} y={textY} textAnchor="middle" dominantBaseline="middle" fontSize="16">{seg.icon}</text>
                              </g>
                            );
                          })}
                          <circle cx="100" cy="100" r="18" fill="#1a1a3e" stroke="rgba(255,255,255,0.4)" strokeWidth="2"/>
                        </svg>
                      </div>
                      <div className="wheel-legend">
                        <div className="legend-title">Abilities</div>
                        {ABILITIES.map(ability => (
                          <div key={ability.id} className="legend-item">
                            <span className="legend-icon" style={{ background: ability.color }}>{ability.icon}</span>
                            <span className="legend-text">{ability.name}</span>
                          </div>
                        ))}
                        <div className="legend-item">
                          <span className="legend-icon" style={{ background: '#4B5563' }}>❌</span>
                          <span className="legend-text">No Ability</span>
                        </div>
                      </div>
                    </div>
                    {!isSpinning ? (
                      <button className="spin-btn" onClick={spinWheel}>SPIN!</button>
                    ) : (
                      <div className="spinning-text">🎰 Spinning...</div>
                    )}
                  </>
                )}

                {turnPhase === 'ability-reveal' && currentAbility && (
                  <>
                    <div className="ability-card" style={{ background: currentAbility.color }}>
                      <div className="ability-icon">{currentAbility.icon}</div>
                      <div className="ability-name">{currentAbility.name}</div>
                      <div className="ability-desc">{currentAbility.desc}</div>
                    </div>
                    <button className="btn" onClick={showReady}>Continue →</button>
                  </>
                )}

                {turnPhase === 'ready' && <div className="ready">READY!</div>}

                {turnPhase === 'playing' && (
                  <div className="play-popup">
                    <div className="play-header">
                      <span className="play-avatar" style={{background: currentPlayer?.character?.color}}>
                        <CharacterAvatar character={currentPlayer?.character} size="small" />
                      </span>
                      <span className="play-name">{currentPlayer?.name}</span>
                    </div>
                    <div className={`timer ${timeLeft <= 5 ? 'urgent' : ''}`}>{timeLeft}</div>
                    <div className="hint-txt">Key Letters</div>
                    <div className="letters">
                      {letters.map((l, i) => <span key={i} className={`letter ${currentWord.toUpperCase().includes(l) ? 'used' : ''}`}>{l}</span>)}
                    </div>
                    
                    {/* Live score preview */}
                    {(() => {
                      const wordUpper = currentWord.toUpperCase();
                      let keyCount = 0;
                      const used = [];
                      for (const letter of letters) {
                        if (wordUpper.includes(letter) && !used.includes(letter)) { keyCount++; used.push(letter); }
                      }
                      const wordLen = currentWord.length;
                      const totalKeys = letters.length;
                      let potentialScore = keyCount === 0 ? (wordLen >= 3 ? 1 : 0) : wordLen * keyCount;
                      const hasDouble = currentAbility?.id === 'double_points';
                      if (hasDouble && keyCount > 0) potentialScore *= 2;
                      
                      return (
                        <div className="live-score-section">
                          {wordLen >= 3 && (
                            <div className="potential-score">
                              <span className="score-label">Potential Score:</span>
                              <span className={`score-value ${keyCount === 0 ? 'low' : keyCount === totalKeys ? 'high' : ''}`}>
                                {potentialScore} {potentialScore === 1 ? 'space' : 'spaces'}
                              </span>
                            </div>
                          )}
                          {wordLen >= 3 && (
                            <div className="multiplier-display">
                              {keyCount === 0 ? (
                                <span className="mult-bad">{wordLen} letters × 0 keys = 1 point only</span>
                              ) : (
                                <span className="mult-good">{wordLen} letters × {keyCount} key{keyCount > 1 ? 's' : ''} = {wordLen * keyCount}{hasDouble ? ` × 2 = ${wordLen * keyCount * 2}` : ''}</span>
                              )}
                            </div>
                          )}
                          {keyCount === 0 && wordLen >= 3 && (
                            <div className="key-tip flash">💡 Use key letters to move further! Try including {letters.join(', ')}</div>
                          )}
                          {keyCount > 0 && keyCount < totalKeys && wordLen >= 3 && (
                            <div className="key-tip">💡 Can you use more key letters? Missing: {letters.filter(l => !wordUpper.includes(l)).join(', ')}</div>
                          )}
                          {keyCount === totalKeys && wordLen >= 3 && (
                            <div className="key-tip perfect">🔥 All {totalKeys} key letters used! Maximum multiplier!</div>
                          )}
                        </div>
                      );
                    })()}
                    
                    <div className="ab-btns">
                      {currentAbility?.id === 'scramble' && !currentAbility.used && <button className="ab-btn scram" onClick={useScramble}>🔀 Scramble</button>}
                      {currentAbility?.id === 'hint' && !currentAbility.used && <button className="ab-btn hint" onClick={useHint}>💡 Hint</button>}
                    </div>
                    {hintWord && <div className="hint-disp">Try: "{hintWord}"</div>}
                    
                    <div className="word-box">{currentWord || <span className="placeholder">Type a word...</span>}</div>
                    
                    <div className="keyboard">
                      {[['Q','W','E','R','T','Y','U','I','O','P'],['A','S','D','F','G','H','J','K','L'],['Z','X','C','V','B','N','M']].map((row, ri) => (
                        <div key={ri} className="kb-row">
                          {row.map(key => (
                            <button key={key} className={`key ${letters.includes(key) ? 'bonus' : ''} ${currentWord.toUpperCase().includes(key) && letters.includes(key) ? 'used-bonus' : ''}`} onClick={() => handleKeyPress(key)} disabled={!isTimerRunning}>
                              {key}
                            </button>
                          ))}
                          {ri === 2 && <button className="key backspace" onClick={() => handleKeyPress('BACKSPACE')} disabled={!isTimerRunning}>⌫</button>}
                        </div>
                      ))}
                    </div>
                    
                    <button className="submit-btn" onClick={submitWord} disabled={!isTimerRunning || currentWord.length < 3 || isValidating}>
                      {isValidating ? 'Checking...' : 'SUBMIT'}
                    </button>
                  </div>
                )}

                {turnPhase === 'result' && wordResult && (
                  <>
                    <div className={`result-card ${wordResult.valid ? 'valid' : 'invalid'}`}>
                      <div className="result-icon">{wordResult.valid ? '✅' : '❌'}</div>
                      <div className="result-word">{wordResult.timeOut ? 'Time\'s Up!' : `"${wordResult.word.toUpperCase()}"`}</div>
                      <div className="result-details">
                        {wordResult.valid ? (
                          <>
                            {wordResult.matchCount > 0 ? (
                              <>
                                <div>{wordResult.wordLength} letters × {wordResult.matchCount}x multiplier</div>
                                <div>({wordResult.matchCount} key letter{wordResult.matchCount > 1 ? 's' : ''} used)</div>
                              </>
                            ) : (
                              <>
                                <div>{wordResult.wordLength} letters = 1 point</div>
                                <div>(No key letters used)</div>
                              </>
                            )}
                            {currentAbility?.id === 'double_points' && <div>✨ Double ability = 2x bonus!</div>}
                          </>
                        ) : (
                          wordResult.timeOut ? 'No word submitted in time' : 'Sorry, not a valid word!'
                        )}
                      </div>
                      <div className="result-points">{wordResult.points === 0 ? "You don't move this turn" : `+${wordResult.points} ${wordResult.points === 1 ? 'space' : 'spaces'}!`}</div>
                    </div>
                    <button className="btn" onClick={wordResult.points === 0 ? proceedToNextTurn : applyMove}>
                      {wordResult.points === 0 ? 'Next Player →' : 'Move →'}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {gamePhase === 'game-over' && winner && (
        <div className="game-over">
          <h1 className="title">🎉 WINNER! 🎉</h1>
          <div className="winner-card" style={{ '--wc': winner.character?.color }}>
            <div className="winner-avatar"><CharacterAvatar character={winner.character} size="large" /></div>
            <div className="winner-name">{winner.name}</div>
            <div className="winner-score">Final Score: {winner.score} points</div>
          </div>
          <div className="standings">
            <h3>Final Standings</h3>
            {[...players].sort((a, b) => b.position - a.position).map((p, i) => (
              <div key={p.id} className="stand-row">
                <span style={{ fontWeight: 700, width: 20 }}>#{i + 1}</span>
                <span style={{ fontSize: '1.2rem' }}><CharacterAvatar character={p.character} size="small" /></span>
                <span style={{ flex: 1, fontWeight: 600 }}>{p.name}</span>
                <span style={{ color: '#94a3b8', fontSize: '.75rem' }}>Sq:{p.position}</span>
              </div>
            ))}
          </div>
          <button className="btn" onClick={startNewGame}>Play Again</button>
        </div>
      )}
    </div>
  );
}
